import {
  CModal,
  CModalBody,
  CModalFooter,
  CFormLabel,
  CFormInput,
  CButton,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
  CModalHeader,
  CModalTitle,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CFormTextarea,
  CAccordionItem,
  CAccordionHeader,
  CAccordionBody,
  CTable,
  CTableBody,
  CTableRow,
  CTableDataCell,
  CAccordion,
  CSpinner,
} from '@coreui/react'
import React, { startTransition, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import FacilityCRUD from '../../services/general/getFacilityData.api.js'
import PatientInfo from './PatientInfo.jsx'
import './Patient.css'
import './dashboard.css'
import CustomAgGrid from './../../components/shared/CustomAgGrid'
import GetDiagnosisData from '../../services/general/getDiagnosisData.api'
import Select from 'react-select'
import AddPatientForm from './AddNewPatient'
import SavePatients from '../../services/general/savePatientinformation.api'
import InsuranceTab from './InsuaranceTab.jsx'
import fetchPatientData from '../../services/general/patientGeneralAPI.api.js'
import PatientInsuranceFn from '../../services/general/PatientInsuranceAPI.api.js'
import EditIcon from '@material-ui/icons/Edit'
import VisibilityIcon from '@material-ui/icons/Visibility'
import DeleteIcon from '@material-ui/icons/Delete'
import ExitToAppIcon from '@material-ui/icons/ExitToApp'
import SearchIcon from '@mui/icons-material/Search'
import Button from '@mui/material/Button'
import Pageloader from '../../components/shared/Pageloader.jsx'
import PatientPopup from './PatientPopup.jsx'
import SaveExtra from '../../services/general/saveExtraData.api.js'
import { motion } from 'framer-motion'
import { AlignLeft, CalendarCheck, EllipsisVertical, FilePenLineIcon, Table2, TrainFrontTunnel, Undo2, X } from 'lucide-react'
import { FunnelPlotOutlined } from '@ant-design/icons'
import PatientNoteDataCRUD from '../../../src/services/general/patientNoteData.api.js'
import toast from 'react-hot-toast'
import { toastConfig } from '../../../src/constants/other.config.js'
import { HiExclamationCircle, HiEye, HiPencil } from 'react-icons/hi'
import { Schedule } from '@mui/icons-material'
import PatientSuperBillData from '../../services/general/patientSuperBill.api.js'
import RescheduleDatePicker from '../../components/shared/RescheduleDatePicker.jsx'
import CustomDatePickingFormat from '../../components/shared/CustomDatePickingFormat.jsx'
import PatientPopSignedChart from './PatientPopupSignedChart.jsx'
import { getAllAppointmentsSnapshot } from '../../components/shared/appointmentFilterData.jsx'
import PatientNoteViewInModalFreeType from './PatientNoteViewInModalFreeType.jsx'
import { formatFacilityOption, getPatientFacilityParentCompany } from '../../utils/facilityParentCompany'

const normalizeSearchValue = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()

const appointmentMatchesSearch = (patient, searchValue) => {
  const normalizedSearchValue = normalizeSearchValue(searchValue)

  if (!normalizedSearchValue) return true

  const fullName = [patient?.FirstName, patient?.MiddleName, patient?.LastName].filter(Boolean).join(' ')
  const firstLastName = [patient?.FirstName, patient?.LastName].filter(Boolean).join(' ')

  const searchableFields = [
    fullName,
    firstLastName,
    patient?.FirstName,
    patient?.MiddleName,
    patient?.LastName,
    patient?.PatientName,
    patient?.patientName,
    patient?.current?.Patient?.label,
    patient?.FacilityName,
    patient?.facilityParentCompany,
    patient?.Room,
    patient?.DOB,
    patient?.PhoneNumber,
    patient?.PatientID,
    patient?.providerAssigned,
    patient?.current?.Provider?.label,
    patient?.data?.ProviderName,
    patient?.data?.PatientName,
    patient?.data?.AppointmentID,
    patient?.data?.StartTime,
    patient?.data?.Status,
    patient?.data?.Type,
    patient?.patientTerm,
    patient?.data?.PatientID, // Add this for newly added patients
    patient?.data?.FirstName, // Add this for newly added patients
    patient?.data?.LastName, // Add this for newly added patients
  ]

  // Filter out null/undefined and convert to string
  const normalizedFields = searchableFields
    .filter((field) => field !== null && field !== undefined)
    .map((field) => normalizeSearchValue(field))

  return normalizedFields.some((field) => field.includes(normalizedSearchValue))
}

const idsMatch = (left, right) => {
  const normalizedLeft = String(left ?? '').trim()
  const normalizedRight = String(right ?? '').trim()
  return Boolean(normalizedLeft && normalizedRight && normalizedLeft === normalizedRight)
}

const safeJsonParse = (value, fallback = null) => {
  if (value === null || value === undefined || value === '') return fallback

  try {
    return JSON.parse(value)
  } catch (error) {
    return fallback
  }
}

const getAppointmentPatientID = (appointment) => appointment?.data?.PatientID ?? appointment?.current?.Patient?.value ?? appointment?.patientID

const getAppointmentFacilityName = (appointment) => appointment?.data?.Facility || appointment?.current?.Facility?.label || appointment?.FacilityName || ''

const getAppointmentPatientStatus = (appointment) => {
  const type = appointment?.data?.Type || appointment?.data?.VisitReason || ''
  const normalizedType = normalizeSearchValue(type)

  if (normalizedType.includes('follow')) return 'Follow Up'
  if (normalizedType.includes('re admit') || normalizedType.includes('re-admit')) return 'Re Admit'
  return 'NewPatient'
}

const buildPatientFromAppointment = (appointment) => {
  const patientName = appointment?.current?.Patient?.label || appointment?.data?.AppointmentName || appointment?.data?.PatientName || ''
  const [firstName = '', ...restName] = patientName.trim().split(/\s+/).filter(Boolean)
  const lastName = restName.length > 0 ? restName.pop() : ''
  const middleName = restName.join(' ')

  return {
    PatientID: getAppointmentPatientID(appointment),
    FirstName: firstName,
    MiddleName: middleName,
    LastName: lastName,
    PatientName: patientName,
    DOB: appointment?.data?.DOB || '',
    Gender: appointment?.data?.Gender || '',
    Age: appointment?.data?.Age || '',
    PhoneNumber: appointment?.data?.PhoneNumber || '',
    EmergencyName: appointment?.data?.EmergencyName || '',
    EmergencyPhone: appointment?.data?.EmergencyPhone || '',
    FacilityID: appointment?.data?.FacilityID || appointment?.current?.Facility?.value || '',
    FacilityName: getAppointmentFacilityName(appointment),
    Room: appointment?.data?.RoomNumber || appointment?.data?.Room || '',
    Status: appointment?.data?.PatientStatus || getAppointmentPatientStatus(appointment),
    Diagnosis: appointment?.data?.Diagnosis || '',
  }
}

const AllAppointmentsList = React.memo(function AllAppointmentsList({
  groupedUpdatedFilteredPatients,
  appointmentsByPatientId,
  onViewPatient,
  onOpenAppointmentDetails,
  onOpenNote,
  onAppointmentAction,
  onOpenReschedule,
}) {
  return (
    <div className="mt-3 all-charts-list">
      <CAccordion alwaysOpen className="border-bottom-0 m-0 w-100 border-0 p-0 dashboard-dark-accordion patient-theme-accordion all-charts-accordion" style={{ borderRadius: '10px' }}>
        {Object.entries(groupedUpdatedFilteredPatients).map(([date, providers], dateIdx) => {
          return (
            <div className="testdiv mb-2" key={date}>
              <div className="border-bottom-0 border-0">
                <div className="border-bottom-0 border-0 mb-2">{date}</div>

                <div className="border-bottom-0 border-to-add-facility card-header bg-none d-flex w-100 border-0 p-0 shadow-sm all-charts-provider-group" style={{ borderRadius: '40px!important' }}>
                  <CAccordion alwaysOpen className="border-bottom-0 m-0 w-100 border-0 p-0 dashboard-dark-accordion patient-theme-accordion all-charts-accordion" style={{ borderRadius: '10px' }}>
                    {Object.entries(providers).map(([providerName, providerPatients], facIdx) => {
                      return (
                        <CAccordionItem className="border-0 border-bottom my-2 mx-4 dashboard-dark-facility-item patient-theme-facility-item all-charts-provider-item" itemKey={1} key={`provider-${dateIdx}-${facIdx}`}>
                          <CAccordionHeader className="patient-theme-accordion-header">
                            {providerName} ({providerPatients?.count})
                          </CAccordionHeader>
                          <CAccordionBody>
                            <CAccordion alwaysOpen className="border-bottom-0 m-0 w-100 border-0 p-0 dashboard-dark-accordion patient-theme-accordion all-charts-accordion" style={{ borderRadius: '10px' }}>
                              {Object.entries(providerPatients?.facilities || {}).map(([facilityName, facilityPatients], facilityIdx) => {
                                const parentCompanyName = facilityPatients.find((patient) => patient?.facilityParentCompany)?.facilityParentCompany || ''

                                return (
                                  <CAccordionItem disabled className="all-charts-facility-item dashboard-dark-facility-item patient-theme-facility-item border-bottom-0 mb-3 shadow-sm border" style={{ borderRadius: '10px' }} key={`fac-${facilityIdx}`}>
                                    <CAccordionHeader className="all-charts-facility-header" style={{ fontSize: '14px', boxShadow: 'unset' }}>
                                      <div className="d-flex align-items-center gap-2 flex-wrap w-100">
                                        <span>
                                          {facilityName} {parentCompanyName && `- ${parentCompanyName}`}
                                        </span>
                                      </div>
                                    </CAccordionHeader>
                                    <CAccordionBody className="all-charts-facility-body border-bottom-0 border-0 py-0 p-0">
                                      <CTable className="all-charts-patient-table patient-theme-table mb-0 border-bottom-0 border-0">
                                        <CTableBody>
                                          {[...(facilityPatients || [])]
                                            .sort((a, b) => {
                                              const aRank = a?.noteStatus === 'Signed' && a?.noteFlag === 'true'
                                              const bRank = b?.noteStatus === 'Signed' && b?.noteFlag === 'true'
                                              return Number(bRank) - Number(aRank)
                                            })
                                            .map((patient, index) => {
                                              const patientAppointments = appointmentsByPatientId.get(String(patient?.PatientID ?? '').trim()) || []
                                              let statusPatientAppointment = ''
                                              let EndTimezone = ''

                                              const scheduledAppointment = patientAppointments.find((p) => {
                                                const { Status, EndTimezone: endTZ } = p?.data || {}
                                                EndTimezone = endTZ
                                                statusPatientAppointment = Status
                                                return Status === 'Scheduled' || Status === 'Rescheduled' || Status === 'Canceled'
                                              })

                                              return (
                                                <CTableRow className={`all-charts-patient-row dashboard-dark-patient-row order-0 px-4 ${patient.Status === 'NewPatient' ? 'NP' : patient.Status === 'Follow Up' ? 'FU' : patient.Status} patient-row-list border-bottom-0 border-0`} key={`${patient.PatientID}-${scheduledAppointment?.data?.AppointmentID || patient.noteIdWhichIsSigned || index}-${index}`} style={{ verticalAlign: 'middle' }}>
                                                  <CTableDataCell className="border-bottom-0 border-0" scope="row" style={{ width: '100%' }}>
                                                    <div
                                                      style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '260px minmax(0, 1fr)',
                                                        alignItems: 'center',
                                                        gap: '16px',
                                                        width: '100%',
                                                      }}
                                                    >
                                                      <span style={{ paddingLeft: '20px', minWidth: 0 }}>
                                                        <span className="patient-name-fixed">{patient.FirstName + ' ' + patient.MiddleName + ' ' + patient.LastName}</span>
                                                      </span>
                                                      <div
                                                        style={{
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          gap: '12px',
                                                          minWidth: 0,
                                                        }}
                                                      >
                                                        <span className="room-pill" style={{ margin: 0, width: '180px', minWidth: '180px', maxWidth: '180px' }}>
                                                          Room No: {patient?.Room}
                                                        </span>
                                                        <span className="notestatus-pill" style={{ margin: 0 }}>
                                                          {patient?.noteStatus}
                                                        </span>
                                                      </div>
                                                    </div>
                                                  </CTableDataCell>

                                                  <CTableDataCell className="d-flex justify-content-end border-bottom-0 border-0">
                                                    <div className="card-header all-charts-row-actions border-0 pb-0 m-0 p-0 d-flex align-items-center gap-2">
                                                      <CButton onClick={() => onOpenAppointmentDetails(patient)} variant="outline" color="success" className="px-2">
                                                        <HiEye size={20} />
                                                      </CButton>
                                                      <CButton
                                                        onClick={() => {
                                                          onOpenNote(patient, scheduledAppointment?.data?.AppointmentID)
                                                        }}
                                                        variant="outline"
                                                        color="dark"
                                                        className="px-2"
                                                      >
                                                        <FilePenLineIcon size={20} />
                                                      </CButton>
                                                      <CDropdown>
                                                        <CDropdownMenu>
                                                          <CDropdownItem onClick={() => onAppointmentAction(scheduledAppointment?.data?.AppointmentID, 'cancel', patient?.PatientID, patient)}>Cancel</CDropdownItem>
                                                          <CDropdownItem onClick={() => onAppointmentAction(scheduledAppointment?.data?.AppointmentID, 'finish', patient?.PatientID, patient)} disabled={!scheduledAppointment?.data?.NoteID}>
                                                            Finish
                                                          </CDropdownItem>
                                                          <CDropdownItem
                                                            onClick={() => {
                                                              onOpenReschedule({
                                                                appointmentID: scheduledAppointment?.data?.AppointmentID,
                                                                patientID: patient?.PatientID,
                                                                patient,
                                                                index,
                                                              })
                                                            }}
                                                          >
                                                            Reschedule
                                                          </CDropdownItem>
                                                          <CDropdownItem onClick={() => onAppointmentAction(scheduledAppointment?.data?.AppointmentID, 'delete', patient?.PatientID, patient)}>Delete</CDropdownItem>
                                                        </CDropdownMenu>
                                                      </CDropdown>
                                                    </div>
                                                  </CTableDataCell>
                                                </CTableRow>
                                              )
                                            })}
                                        </CTableBody>
                                      </CTable>
                                    </CAccordionBody>
                                  </CAccordionItem>
                                )
                              })}
                            </CAccordion>
                          </CAccordionBody>
                        </CAccordionItem>
                      )
                    })}
                  </CAccordion>
                </div>
              </div>
            </div>
          )
        })}
      </CAccordion>
    </div>
  )
})

const AllAppointments2 = () => {
  const allChartsThemeRef = useRef(null)
  const scheduleBootstrapPromiseRef = useRef(null)
  const hasScheduleBootstrapRef = useRef(false)
  const handleAppointmentActionRef = useRef(null)
  const [patientIDClicked, setPatientIDClicked] = useState(null)
  const [rowClicked, setRowClicked] = useState(null)
  const [selectedFacility, setSelectedFacility] = useState({})
  const [roomNumber, setRoomNumber] = useState('')
  const [insuranceTypes, setInsuranceTypes] = useState([])
  const [boxView, setBoxView] = useState('list')
  const [addedInsurances, setAddedInsurances] = useState([])
  const [insuranceList, setInsuranceList] = useState([])
  const [selectedDetails, setSelectedDetails] = useState({})
  const [activeTab, setActiveTab] = useState('patientDetails')
  const buttonList = [{ Name: 'View' }, { Name: 'Edit' }, { Name: 'Delete' }]
  const [patientID, setPatientID] = useState('')
  const [diagnosisData, setDiagnosisData] = useState([])
  var selectedDiagnosis = []
  const [allSelectedDiagnosis, setAllSelectedDiagnosis] = useState([])
  const [facilities, setFacilities] = useState([])
  const [patientData, setPatientData] = useState('')
  const [providerList, setProviderList] = useState([])
  const [viewPatientDetail, setViewPatientDetail] = useState()
  const [isPageLoading, setIsPageLoading] = useState(false)
  const [patientModelVisible, setPatientModalVisible] = useState(false)
  const [dataCallType, setDataCallType] = useState(null)
  const [setDataId, setEditDataId] = useState(null)
  const [showPatientInfo, setShowPatientInfo] = useState(false)
  const [deleteVisible, setDeleteVisible] = useState(false)
  const [deletePatientID, setDeletePatientID] = useState(null)
  const [appointment, setAppointment] = useState([])
  const [appointmentPatient, setAppointmentPatient] = useState(null)
  const [appointments, setAppointments] = useState([])
  const [showNoteJustThisId, setShowNoteJustThisId] = useState(null)
  const [noteID, setNoteID] = useState(null)
  const [cancelModel, setCancelModel] = useState(false)
  const [finishModel, setFinishModel] = useState(false)
  const [appointmentDataID, setAppointmentDataID] = useState(null)
  const [appointmentID, setAppointmentID] = useState(null)
  const [allAppointment, setAllAppointment] = useState([])
  const [noteStatus, setNoteStatus] = useState('New Note')
  const [patientAppointmentID, setPatientAppointmentID] = useState(null)
  const [patientId, setPatientId] = useState(null)
  const [creatingNewNote, setCreatingNewNote] = useState(false)
  const [isNoteModalLoading, setIsNoteModalLoading] = useState(false)
  const [superBill, setSuperBill] = useState([])
  const [scheduleTime, setScheduleTime] = useState(null)
  const [deleteModel, setDeleteModel] = useState(false)
  const [hasNotAppointment, setHasNotAppointment] = useState(false)
  const [histories, setHistories] = useState({
    pmhx: [],
    pshx: [],
    fh: [],
    sh: [],
  })
  const [childformData, setChildFormData] = useState({
    FirstName: '',
    MiddleName: '',
    LastName: '',
    DOB: '',
    Gender: '',
    Age: '0',
    PhoneNumber: '',
    EmergencyName: '',
    EmergencyPhone: '',
    Address: '',
    State: '',
    City: '',
    ZipCode: '',
    Country: '',
    insurance: '',
  })

  useEffect(() => {
    const syncAllChartsTheme = () => {
      if (allChartsThemeRef.current) {
        allChartsThemeRef.current.dataset.theme = localStorage.getItem('provio_theme') || 'dark'
      }
    }

    syncAllChartsTheme()
    window.addEventListener('storage', syncAllChartsTheme)
    window.addEventListener('provio-theme-change', syncAllChartsTheme)

    return () => {
      window.removeEventListener('storage', syncAllChartsTheme)
      window.removeEventListener('provio-theme-change', syncAllChartsTheme)
    }
  }, [])

  const resetStates = () => {
    setChildFormData({
      FirstName: '',
      MiddleName: '',
      LastName: '',
      DOB: '',
      Gender: '',
      Age: '0',
      PhoneNumber: '',
      EmergencyName: '',
      EmergencyPhone: '',
      Address: '',
      State: '',
      City: '',
      ZipCode: '',
      Country: '',
      insurance: '',
    })
    setRoomNumber('')
    setSelectedFacility({})
    setAddedInsurances([])
    setInsuranceList([])
    setSelectedDetails({})
    setAllSelectedDiagnosis([])
    setModalVisible(false)
  }

  const [daysMap, setDaysMap] = useState({})
  const [locations, setLocations] = useState([])
  const [cancelReason, setCancelReason] = useState('')

  const [notesStatusOptions, setNotesStatusOptions] = useState([
    { label: 'Finished', value: 'Finished' },
    { label: 'Cancelled', value: 'Canceled' },
  ])

  const patientSelected = async (rowSelected) => {
    setViewPatientDetail(rowSelected)

    let patientInsurance = await PatientInsuranceFn('SELECTALL', '', rowSelected.PatientID)

    try {
      patientInsurance = JSON.parse(patientInsurance.d)?.Data
    } catch (e) {}

    try {
      let tempSelectedDiagnosis = rowSelected.Diagnosis ? rowSelected.Diagnosis.split(',') : []
      selectedDiagnosis = diagnosisData.filter((obj) => tempSelectedDiagnosis.includes(obj.label))
      setRoomNumber(rowSelected?.Room)
      const userFacilities = facilities.find((item) => item.label === rowSelected?.FacilityName)
      setSelectedFacility(userFacilities ? userFacilities : facilities[0])
      setAllSelectedDiagnosis(selectedDiagnosis)
    } catch (e) {
      setAllSelectedDiagnosis([])
    }

    setChildFormData({
      FirstName: rowSelected.FirstName || '',
      MiddleName: rowSelected.MiddleName || '',
      LastName: rowSelected.LastName || '',
      DOB: rowSelected.DOB || '',
      Gender: rowSelected.Gender || '',
      Age: rowSelected.Age || '0',
      PhoneNumber: rowSelected.PhoneNumber || '',
      EmergencyName: rowSelected.EmergencyName || '',
      EmergencyPhone: rowSelected.EmergencyPhone || '',
      Address: rowSelected.Address || '',
      State: rowSelected.State || '',
      City: rowSelected.City || '',
      ZipCode: rowSelected.ZipCode || '',
      Country: rowSelected.Country || '',
    })

    setSelectedDetails({ Insurances: patientInsurance })
  }

  const openTreatmentNote = useCallback((rowSelected, appointmentID) => {
    setPatientIDClicked(null)
    setRowClicked(null)
    setPatientAppointmentID(null)
    setShowNoteJustThisId(null)
    setIsNoteModalLoading(true)
    setPatientModalVisible(true)
    window.setTimeout(() => {
      startTransition(() => {
        setPatientID(rowSelected?.PatientID)
        setPatientIDClicked(rowSelected?.PatientID)
        setRowClicked(rowSelected)
        setPatientAppointmentID(appointmentID)
        setShowNoteJustThisId(rowSelected?.noteIdWhichIsSigned ?? null)
      })
    }, 0)
  }, [])

  const hasSelectedNotePatient = Boolean(patientModelVisible && patientIDClicked && rowClicked?.PatientID && patientAppointmentID)
  // const getAppointmentData = async () => {
  //     setIsPageLoading(true)
  //     try {

  //         let data = await SaveExtra('Get_By_Type', 'AppointmentsData', '', '')
  //         data = JSON.parse(data.d)?.Data || [];

  //         if (data) {
  //             data = data?.map((item) => {
  //                 return {
  //                     Id: item.Id,
  //                     Data: JSON.parse(item?.JsonData)
  //                 }
  //             })

  //         }
  //         setAppointmentDataID(data)
  //         setAppointments(data[0]?.Data)
  //         return data[0]?.Data || []
  //     } catch (error) {
  //         console.log(error)
  //     } finally {
  //         setIsPageLoading(false);

  //     }
  // };

  const isManuallyScheduledAppointment = (appointment) => {
    const status = appointment?.data?.Status
    const scheduleBy = appointment?.data?.ScheduleBy
    const type = appointment?.data?.Type
    const isInFacility = appointment?.data?.isInFacility
    const priorAuth = appointment?.data?.otherDetails?.priorAuth?.isRequired

    let priorAuthTime = 0
    if (priorAuth) {
      priorAuthTime = parseInt(appointment?.data?.otherDetails?.priorAuth?.details?.noOfTimes, 10)
    }

    const isManualOrFollowUp = scheduleBy !== 'auto' || (type === 'Follow Up' && isInFacility && priorAuth && priorAuthTime > 0)
    const isScheduledOrRescheduled = status === 'Scheduled' || status === 'Rescheduled' || status == ''

    return isScheduledOrRescheduled && isManualOrFollowUp
  }

  // const getAppointmentData = async () => {
  //   setIsPageLoading(true)
  //   try {
  //     const result = await SaveExtra('Get_By_Type', 'multiAppointmentsData', '', '')
  //     const rawData = JSON.parse(result.d)?.Data || []

  //     let structuredData = []

  //     rawData.forEach((item) => {
  //       let parsedList = JSON.parse(item?.JsonData)

  //       if (Array.isArray(parsedList)) {
  //         parsedList.forEach((parsedItem) => {
  //           const patientID = parsedItem?.patientID
  //           const appointmentDetails = parsedItem?.appointmentDetails || []

  //           const formattedAppointments = appointmentDetails.map((appointment) => {
  //             const data = appointment?.data || {}
  //             const current = appointment?.current || {
  //               Patient: { value: data?.PatientID || '', label: data?.AppointmentName || 'Unknown' },
  //               Provider: { value: '', label: data?.ProviderName || 'Unknown' },
  //               Facility: { value: '', label: data?.Facility || 'Unknown' },
  //               Insurance: data?.Insurance || {},
  //               otherDetails: data?.otherDetails || {},
  //             }

  //             return { data, current }
  //           })

  //           structuredData.push({
  //             patientID,
  //             appointmentDetails: formattedAppointments,
  //           })
  //         })
  //       } else {
  //         console.warn('Unexpected non-array parsed data:', parsedList)
  //       }
  //     })

  //     setAllAppointment(structuredData)

  //     let flatAppointments = structuredData.flatMap((p) => p.appointmentDetails)
  //     const AppointmentCount = flatAppointments.filter((item) => isManuallyScheduledAppointment(item))

  //     setPatientCount(AppointmentCount.length)

  //     setAppointments(flatAppointments)

  //     return flatAppointments
  //   } catch (error) {
  //     console.log('Error in getAppointmentData', error)
  //   } finally {
  //     setIsPageLoading(false)
  //   }
  // }

  const handleAllAppointmentsData = async (type, byDate) => {
    const { appointmentData, appointmentDetails } = await getAllAppointmentsSnapshot(type, byDate)
    setAllAppointment(appointmentData)
    setAppointments(appointmentDetails)
    return appointmentDetails
  }

  const getAppointmentData = async () => {
    setIsPageLoading(true)
    try {
      const flatAppointments = await handleAllAppointmentsData('GET')
      return flatAppointments
    } catch (error) {
      console.log('Error in getAppointmentData', error)
    } finally {
      setIsPageLoading(false)
    }
  }
  const getAppointmentForAutoSchedule = async () => {
    setIsPageLoading(true)
    try {
      const result = await SaveExtra('Get_By_Type', 'multiAppointmentsData', '', '')
      const rawData = JSON.parse(result.d)?.Data || []

      let structuredData = []

      rawData.forEach((item) => {
        let parsedList = JSON.parse(item?.JsonData)

        if (Array.isArray(parsedList)) {
          parsedList.forEach((parsedItem) => {
            const patientID = parsedItem?.patientID
            const appointmentDetails = parsedItem?.appointmentDetails || []

            const formattedAppointments = appointmentDetails.map((appointment) => {
              const data = appointment?.data || {}
              const current = appointment?.current || {
                Patient: { value: data?.PatientID || '', label: data?.AppointmentName || 'Unknown' },
                Provider: { value: '', label: data?.ProviderName || 'Unknown' },
                Facility: { value: '', label: data?.Facility || 'Unknown' },
                Insurance: data?.Insurance || {},
                otherDetails: data?.otherDetails || {},
              }

              return { data, current }
            })

            structuredData.push({
              patientID,
              appointmentDetails: formattedAppointments,
            })
          })
        } else {
          console.warn('Unexpected non-array parsed data:', parsedList)
        }
      })

      return structuredData
    } catch (error) {
      console.log('Error in getAppointmentData', error)
    } finally {
      setIsPageLoading(false)
    }
  }

  const patientDataFn = async () => {
    setHasNotAppointment(false)
    setIsPageLoading(true)
    try {
      const [patientData, appointments] = await Promise.all([SavePatients('GetAll', '', '', ''), getAppointmentData()])
      const patient = JSON.parse(patientData.d).Data
      let schedulePatient = patient?.filter((patient) => appointments.find((item) => idsMatch(getAppointmentPatientID(item), patient.PatientID)))

      setPatientData(schedulePatient)
      setAppointment(appointments)
      setHasNotAppointment(appointments.length === 0)

      let count = schedulePatient.length
      // setPatientCount(count)
    } catch (error) {
      console.log(error)
    } finally {
      setIsPageLoading(false)
    }
  }
  const getData = async () => {
    let a = await GetDiagnosisData()
    let data = JSON.parse(a.d)
    let options = []
    data.Data.map((obj) => {
      options.push({
        value: obj.id,
        label: obj.Name.trim(),
      })
    })
    setDiagnosisData(options)

    let insuranceData = await fetchPatientData('GET', 'Insurance', '{}')
    insuranceData = JSON.parse(insuranceData.d)?.Data

    const uniqueInsuranceTypes = Array.from(new Set(insuranceData.map((ins) => ins.Type)))
    setInsuranceList(insuranceData)
    setInsuranceTypes(uniqueInsuranceTypes)
  }

  const getFacilities = async () => {
    let a = await FacilityCRUD('SELECT', {})
    let data = JSON.parse(a.d)?.Data
    try {
      const formattedData = data.map(formatFacilityOption)
      setFacilities(formattedData)
      setSelectedFacility(formattedData[0])
    } catch (e) {}
  }

  const [modalVisible, setModalVisible] = useState(false)
  const [viewModalVisible, setViewModalVisible] = useState(false)

  const handleonClick = () => {
    setModalVisible((e) => !e)
  }

  const patientSelectedFacility = async (rowSelected) => {
    try {
      const userFacilities = facilities.find((item) => item.label === rowSelected?.FacilityName)
      // setSelectedFacility(userFacilities ? userFacilities : facilities[0]);
      return userFacilities ? userFacilities : facilities[0]
    } catch (e) {}
  }

  const saveDiagnosisData = async (customData) => {
    let a = {}
    let facility = await patientSelectedFacility(customData)
    try {
      a['FacilityID'] = facility?.value.toString() || selectedFacility?.value.toString()
      a['FacilityName'] = facility?.label.toString() || selectedFacility?.label.toString()
      a['Room'] = customData?.Room?.toString() || roomNumber.toString()
    } catch (e) {
      a['FacilityID'] = ''
      a['FacilityName'] = ''
      a['Room'] = -1
    }

    // let allDiagnosis = allSelectedDiagnosis.map(obj => { return obj.label }).join(",");

    let allDiagnosis = customData?.Diagnosis || ''

    let insurances = addedInsurances.map((ins) => ({
      InsuranceID: ins?.ID,
    }))

    let savePatientInfo
    if (patientID && patientID !== '') {
      savePatientInfo = SavePatients('Update', JSON.stringify(customData ? customData : childformData), JSON.stringify(a), allDiagnosis, patientID, JSON.stringify(insurances))
    }

    // await savePatientInfo;

    await patientDataFn()
    // resetStates()
  }

  useEffect(() => {
    getData()
    patientDataFn()
    getFacilities()
  }, [])

  const noteStructureRef = useRef({})

  // if (rowClicked) {
  //   let provideInformation = rowClicked.Diagnosis.split(',');
  //   let information = provideInformation.map((item) => {
  //     return (
  //       { diagnosis: item, information: 'patient' }
  //     )
  //   })

  //   noteStructureRef.current = {
  //     ...noteStructureRef.current,
  //     information,
  //   }

  // }

  const handleDeletePatient = async () => {
    let patientData = await SavePatients('Delete', '', '', '', deletePatientID, '')
    await patientDataFn()
    setDeletePatientID(null)
    setDeleteVisible(false)
  }

  const handleClicksPatiens = useCallback((patient) => {
    setPatientIDClicked(patient?.PatientID)
    setPatientID(patient?.PatientID)
    setRowClicked(patient)
    setShowPatientInfo(true)
  }, [])
  const handleOpenAppointmentDetails = useCallback((patient, appointmentID) => {
    setActiveKey(2)
    setSelectedAppointmentDetailsId(appointmentID ?? null)
    setShowPatientAppointmentId(patient?.PatientID ?? null)
  }, [])
  const [showFilter, setShowFilter] = useState(false)
  const [filterData, setFilterData] = useState({
    facility: [],
    scheduleDate: '',
    noteStatuses: [],
    searchValue: '',
    date: '',
    status: [],
    providers: [],
  })
  const deferredFilterData = useDeferredValue(filterData)
  const updateFilterData = useCallback((updater) => {
    startTransition(() => {
      setFilterData((prev) => (typeof updater === 'function' ? updater(prev) : updater))
    })
  }, [])
  const selectStatusOption = [
    { value: 'NewPatient', label: 'New Patient' },
    { value: 'Follow Up', label: 'Follow Up' },
    { value: 'Re Admit', label: 'Re Admit' },
  ]

  const selectedOption = selectStatusOption.find((option) => option.value === filterData.status)

  // const GetPatientTreatmentsNotes = async () => {
  //   if (!patientData) return

  //   try {
  //     const updatedPatients = await Promise.all(
  //       patientData.map(async (patient) => {
  //         let status = patient.noteStatus || 'Not Started' // Default status
  //         let flag = 'false' // Default flag
  //         let noteHasSubmiitedOrSigned = false

  //         try {
  //           const dataResponse = await PatientNoteDataCRUD('GET', patient?.PatientID)
  //           const parsedData = JSON.parse(dataResponse?.d)?.Data

  //           const findIsThereAnySubmiitedOrSigned = parsedData.find((item) => item?.status === 'Finished' || item?.status === 'Signed') //dont include the cancelled

  //           if (findIsThereAnySubmiitedOrSigned) {
  //             noteHasSubmiitedOrSigned = true
  //           }

  //           if (findIsThereAnySubmiitedOrSigned) {
  //             const findAppointment = appointment.find((item) => item?.data?.PatientID === patient?.PatientID)

  //             if (findAppointment) {
  //               const noteIdForthisAppointment = findAppointment?.data?.NoteID
  //               const findNote = parsedData.find((item) => item?.id === noteIdForthisAppointment)
  //               if (findNote) {
  //                 status = findNote?.status || status
  //                 flag = findNote?.flag || 'false'
  //               }
  //             }

  //             return {
  //               providerAssigned: findAppointment?.current?.Provider?.label,
  //               // scheduleStartTime: findAppointment?.data?.StartTime,
  //               // appointmentStatus: findAppointment?.data?.Status,
  //               // patientTerm: findAppointment?.data?.term || '', //long terms or short terms means patient type
  //               // ...(typeof findAppointment?.data?.patientSeen === 'boolean' && {
  //               //   patientSeen: findAppointment.data.patientSeen,
  //               // }),
  //               ...patient,
  //               noteStatus: status,
  //               noteFlag: flag,
  //               noteHasSubmiitedOrSigned,
  //             }
  //           }
  //         } catch (err) {
  //           console.error(`Error fetching notes for patient ${patient?.PatientID}:`, err)
  //         }
  //       }),
  //     )

  //     setFilteredPatientData(updatedPatients.filter(Boolean).filter((item) => item.noteHasSubmiitedOrSigned))
  //   } catch (error) {
  //     console.error('Error fetching patient orders', error)
  //     toast.error('Error in patient treatment', toastConfig)
  //   }
  // }

  const appointmentPatients = useMemo(() => {
    if (!patientData || !appointment) return []

    const patientsById = new Map((patientData || []).map((patient) => [String(patient?.PatientID), patient]))

    return (appointment || [])
      .map((appt) => {
        const appointmentPatientId = getAppointmentPatientID(appt)
        const patient = patientsById.get(String(appointmentPatientId)) || buildPatientFromAppointment(appt)
        const patientID = patient?.PatientID || appointmentPatientId
        if (!patientID) return null

        return {
          ...patient,
          ...appt,
          PatientID: patientID,
          FacilityID: patient?.FacilityID || appt?.data?.FacilityID || appt?.current?.Facility?.value || '',
          FacilityName: patient?.FacilityName || getAppointmentFacilityName(appt),
          Room: patient?.Room || appt?.data?.RoomNumber || appt?.data?.Room || '',
          Status: patient?.Status || getAppointmentPatientStatus(appt),
          noteStatus: appt?.data?.Status || 'Not Created',
          noteFlag: 'false',
          noteIdWhichIsSigned: appt?.data?.NoteID || null,
          providerAssigned: appt?.current?.Provider?.label,
          scheduleStartTime: appt?.data?.StartTime,
          appointmentStatus: appt?.data?.Status,
          patientTerm: appt?.data?.term || '',
          facilityParentCompany: getPatientFacilityParentCompany({ ...patient, ...appt }, facilities),
          ...(typeof appt?.data?.patientSeen === 'boolean' && {
            patientSeen: appt.data.patientSeen,
            notSeenReason: appt.data.notSeenReason,
          }),
        }
      })
      .filter(Boolean)
  }, [patientData, appointment, facilities])

  const getAllProviderData = async () => {
    try {
      // Fetch provider data
      let providerData = await fetchPatientData('SELECT', 'Provider', '{}')
      providerData = safeJsonParse(providerData?.d, {})?.Data || []
      setProviderList(providerData.sort((a, b) => a.Name.localeCompare(b.Name)))
    } catch (error) {
      console.log(error)
    } finally {
    }
  }
  useEffect(() => {
    getAllProviderData()
  }, [])
  const { updatedFilteredPatients, groupedUpdatedFilteredPatients, patientCount, patientInfoCount } = useMemo(() => {
    const hasSearchValue = Boolean(normalizeSearchValue(deferredFilterData.searchValue))
    const selectedFacilities = facilities.filter((facility) => deferredFilterData.facility.includes(facility.value))
    const selectedFacilityLabelSet = new Set(selectedFacilities.map((facility) => normalizeSearchValue(facility.label)).filter(Boolean))
    const selectedFacilityIDSet = new Set(selectedFacilities.map((facility) => String(facility.value ?? '').trim()).filter(Boolean))
    const selectedProviderSet = new Set(deferredFilterData.providers)
    const selectedNoteStatusSet = new Set(deferredFilterData.noteStatuses)
    const selectedStatusSet = new Set(deferredFilterData.status)

    const updatedFilteredPatientss = (appointmentPatients || [])
      .filter(Boolean)
      .map((patient) => ({
        ...patient,
        facilityParentCompany: getPatientFacilityParentCompany(patient, facilities),
      }))
      .filter((patient) => {
        const patientFacilityID = patient?.FacilityID || patient?.current?.Facility?.value || patient?.data?.FacilityID
        const matchesFacility =
          deferredFilterData.facility.length === 0 ||
          selectedFacilityIDSet.has(String(patientFacilityID ?? '').trim()) ||
          selectedFacilityLabelSet.has(normalizeSearchValue(patient.FacilityName))

        const matchesProviders = selectedProviderSet.size === 0 || selectedProviderSet.has(patient?.providerAssigned)

        const matchesNoteStatuses = selectedNoteStatusSet.size === 0 || selectedNoteStatusSet.has(patient?.noteStatus)

        const matchesStatus = selectedStatusSet.size === 0 || selectedStatusSet.has(patient?.Status)

        const matchesSearchValue = appointmentMatchesSearch(patient, deferredFilterData.searchValue)

        const matchesDate = !deferredFilterData.date || patient?.DOB === deferredFilterData.date

        // const patientDate = new Date(patient?.scheduleStartTime)
        // const formattedPatientDate = `${patientDate.getFullYear()}-${(patientDate.getMonth() + 1).toString().padStart(2, '0')}-${patientDate.getDate().toString().padStart(2, '0')}`

        // const matchesSchedule = !filterData.scheduleDate || formattedPatientDate === filterData.scheduleDate
        const matchesSchedule = !deferredFilterData.scheduleDate || patient?.scheduleStartTime?.split?.(' ')?.[0] === deferredFilterData.scheduleDate

        // const matchesSchedule = !filterData.scheduleDate || patient?.scheduleStartTime === filterData.scheduleDate

        return matchesFacility && matchesStatus && matchesSearchValue && matchesDate && matchesProviders && matchesSchedule && matchesNoteStatuses
      })

    const patientInfoCountData = {
      followUp: updatedFilteredPatientss.filter((item) => item?.Status === 'Follow Up').length,
      newPatient: updatedFilteredPatientss.filter((item) => item?.Status === 'NewPatient').length,
    }

    const providersToShow = selectedProviderSet.size > 0 ? providerList.filter((p) => selectedProviderSet.has(p.Name)) : providerList
    const groupedByDateProviderFacility = {}

    updatedFilteredPatientss.forEach((patient) => {
      const date = patient?.scheduleStartTime?.split?.(' ')?.[0] || 'Unknown Date'
      const providerName = patient?.providerAssigned || 'Unknown Provider'
      const facilityKey = patient?.FacilityName || 'Unknown Facility'

      groupedByDateProviderFacility[date] ||= {}

      if (!hasSearchValue) {
        providersToShow.forEach((p) => {
          const name = p?.Name
          if (!name) return

          groupedByDateProviderFacility[date][name] ||= {
            count: 0,
            facilities: {},
          }
        })
      }

      groupedByDateProviderFacility[date][providerName] ||= {
        count: 0,
        facilities: {},
      }

      const providerNode = groupedByDateProviderFacility[date][providerName]

      providerNode.facilities[facilityKey] ||= []

      providerNode.facilities[facilityKey].push(patient)

      providerNode.count += 1
    })

    return {
      updatedFilteredPatients: updatedFilteredPatientss,
      groupedUpdatedFilteredPatients: groupedByDateProviderFacility,
      patientCount: updatedFilteredPatientss.length,
      patientInfoCount: patientInfoCountData,
    }
  }, [appointmentPatients, deferredFilterData, facilities, providerList])
  const appointmentsByPatientId = useMemo(() => {
    const appointmentMap = new Map()

    ;(appointment || []).forEach((item) => {
      const patientIdKey = String(getAppointmentPatientID(item) ?? '').trim()
      if (!patientIdKey) return

      const existingAppointments = appointmentMap.get(patientIdKey)
      if (existingAppointments) {
        existingAppointments.push(item)
      } else {
        appointmentMap.set(patientIdKey, [item])
      }
    })

    return appointmentMap
  }, [appointment])

  // Patient Appointments
  const [showPatientAppointmentId, setShowPatientAppointmentId] = useState(null)
  const [selectedAppointmentDetailsId, setSelectedAppointmentDetailsId] = useState(null)
  const [patientAppointment, setPatientAppointment] = useState([])
  const [activeKey, setActiveKey] = useState(1)
  const scheduledPatientAppointments = useMemo(() => {
    return (patientAppointment || []).filter((item) => item?.data?.Status === 'Scheduled' || item?.data?.Status === 'Rescheduled')
  }, [patientAppointment])

  const GetSelectedPatientAppoint = async () => {
    try {
      const selectedAppointments = appointment.filter((appoint) => appoint?.data?.AppointmentID === selectedAppointmentDetailsId)
      setPatientAppointment(selectedAppointments)
    } catch (error) {
      console.error('Error fetching patient appointments:', error)
      toast.error('Error fetching patient appointments', toastConfig)
    }
  }

  // const handleAppointmentSubmit = async () => {
  //     try {
  //         setIsPageLoading(true)
  //         let appointmentFilterData = appointments.filter(item => (item.data.AppointmentID !== appointmentID))
  //         await SaveExtra('Update', 'AppointmentsData', JSON.stringify(appointmentFilterData), appointmentDataID[0]?.Id);
  //          patientDataFn()

  //         if (cancelModel) {
  //             toast.success('Cancel Appointment Successfully', toastConfig)
  //         } else if(finishModel) {
  //             toast.success('Finish Appointment Successfully', toastConfig)
  //         }
  //         // toast.success('Cancel Appointment Successfully', toastConfig)
  //     } catch (error) {
  //         toast.error('Failed to save', toastConfig)
  //     } finally {
  //         setIsPageLoading(false)
  //         setCancelModel(false)
  //         setFinishModel(false)
  //         setAppointmentID(null)
  //     }
  // }

  const handleAppointmentSubmit = async (type, appointmentID, patientId) => {
    try {
      setIsPageLoading(true)
      const ChangeNoteStatus = handlePatientNote('Finished', appointmentID, patientId)
      const groupedData = []
      let appointmentFilterData = appointments.map((item) => {
        if (item.data.AppointmentID === appointmentID) {
          const now = new Date()
          const formattedEndDate = now
            .toLocaleString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })
            .replace(',', '')
          return {
            ...item,
            data: {
              ...item.data,
              Status: type,
              EndTimezone: formattedEndDate,
            },
          }
        }
        return item
      })

      appointmentFilterData.forEach((entry) => {
        const patientID = entry.current.Patient.value

        const existing = groupedData.find((item) => item.patientID === patientID)

        if (existing) {
          existing.appointmentDetails.push({ data: entry.data, current: entry.current })
        } else {
          groupedData.push({
            patientID,
            appointmentDetails: [{ data: entry.data, current: entry.current }],
          })
        }
      })

      await SaveExtra('Update', 'multiAppointmentsData', JSON.stringify(groupedData), allAppointment[0]?.Id || 388)

      await patientDataFn()
      if (cancelModel) {
        toast.success('Finish Appointment Successfully', toastConfig)
      } else {
        // toast.success('Finish Appointment Successfully', toastConfig)
      }
      // toast.success('Cancel Appointment Successfully', toastConfig)
    } catch (error) {
      toast.error('Failed to save', toastConfig)
    } finally {
      setIsPageLoading(false)
      setCancelModel(false)
      setFinishModel(false)
      setAppointmentID(null)
    }
  }

  const handleDeleteSubmit = async () => {
    try {
      setIsPageLoading(true)
      const DeleteNote = deletePatientNote(appointmentID, patientId)
      const groupedData = []
      let appointmentFilterData = appointments.filter((item) => item.data.AppointmentID !== appointmentID)

      appointmentFilterData.forEach((entry) => {
        const patientID = entry.current.Patient.value

        const existing = groupedData.find((item) => item.patientID === patientID)

        if (existing) {
          existing.appointmentDetails.push({ data: entry.data, current: entry.current })
        } else {
          groupedData.push({
            patientID,
            appointmentDetails: [{ data: entry.data, current: entry.current }],
          })
        }
      })

      await SaveExtra('Update', 'multiAppointmentsData', JSON.stringify(groupedData), allAppointment[0]?.Id || 388)

      await patientDataFn()
      toast.success('Delete Appointment Successfully', toastConfig)
    } catch (error) {
      toast.error('Failed to save', toastConfig)
    } finally {
      setIsPageLoading(false)
      setDeleteModel(false)
      setAppointmentID(null)
      setPatientID(null)
    }
  }

  const handleAppointmentCancel = async (type) => {
    try {
      setIsPageLoading(true)
      const ChangeNoteStatus = handlePatientNote('Canceled', appointmentID, patientId)
      const groupedData = []
      let appointmentFilterData = appointments.map((item) => {
        if (item.data.AppointmentID === appointmentID) {
          const now = new Date()
          const formattedEndDate = now
            .toLocaleString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })
            .replace(',', '')
          return {
            ...item,
            data: {
              ...item.data,
              Status: type,
              EndTimezone: formattedEndDate,
            },
          }
        }
        return item
      })

      appointmentFilterData.forEach((entry) => {
        const patientID = entry.current.Patient.value

        const existing = groupedData.find((item) => item.patientID === patientID)

        if (existing) {
          existing.appointmentDetails.push({ data: entry.data, current: entry.current })
        } else {
          groupedData.push({
            patientID,
            appointmentDetails: [{ data: entry.data, current: entry.current }],
          })
        }
      })

      await SaveExtra('Update', 'multiAppointmentsData', JSON.stringify(groupedData), allAppointment[0]?.Id || 388)

      await patientDataFn()
      toast.success('Cancel Appointment Successfully', toastConfig)
    } catch (error) {
      toast.error('Failed to save', toastConfig)
    } finally {
      setIsPageLoading(false)
      setCancelModel(false)
      setFinishModel(false)
      setAppointmentID(null)
    }
  }

  const handleAddNoteId = async (noteID) => {
    try {
      const currentAppointment = appointments.find((item) => item?.data?.AppointmentID === patientAppointmentID)
      if (!currentAppointment || currentAppointment?.data?.NoteID === noteID) {
        return
      }

      setIsPageLoading(true)

      const groupedData = []
      let appointmentFilterData = appointments.map((item) => {
        if (item.data.AppointmentID === patientAppointmentID) {
          return {
            ...item,
            data: {
              ...item.data,
              NoteID: noteID,
            },
          }
        }
        return item
      })

      appointmentFilterData.forEach((entry) => {
        const patientID = entry.current.Patient.value

        const existing = groupedData.find((item) => item.patientID === patientID)

        if (existing) {
          existing.appointmentDetails.push({ data: entry.data, current: entry.current })
        } else {
          groupedData.push({
            patientID,
            appointmentDetails: [{ data: entry.data, current: entry.current }],
          })
        }
      })

      await SaveExtra('Update', 'multiAppointmentsData', JSON.stringify(groupedData), allAppointment[0]?.Id || 388)

      await patientDataFn()

      // toast.success('Cancel Appointment Successfully', toastConfig)
    } catch (error) {
      toast.error('Failed to save Note Id', toastConfig)
    } finally {
      setIsPageLoading(false)
      setPatientAppointment(null)
    }
  }

  const getFormattedDate = () => {
    const today = new Date()
    const dd = String(today.getDate()).padStart(2, '0')
    const mm = String(today.getMonth() + 1).padStart(2, '0') // January is 0!
    const yyyy = today.getFullYear()
    const hh = String(today.getHours()).padStart(2, '0')
    const min = String(today.getMinutes()).padStart(2, '0')
    return `${mm}.${dd}.${yyyy} ${hh}:${min}`
  }

  const handlePatientNote = async (type, appointmentID, patientId) => {
    try {
      if (!appointmentID || !patientId) {
        throw new Error('Missing appointmentId or patientId.')
      }

      const appointmentFilterData = appointments.filter((item) => item?.data?.AppointmentID === appointmentID)

      if (appointmentFilterData.length === 0) {
        throw new Error(`No appointment found with ID: ${appointmentID}`)
      }

      const noteID = appointmentFilterData[0]?.data?.NoteID

      if (!noteID) {
        throw new Error(`NoteID not found for appointment ID: ${appointmentID}`)
      }

      const dataResponse = await PatientNoteDataCRUD('GET', patientId)

      if (!dataResponse?.d) {
        throw new Error('No data received from PatientNoteDataCRUD GET.')
      }

      const parsedResponse = JSON.parse(dataResponse.d)
      const parsedData = parsedResponse?.Data

      if (!Array.isArray(parsedData)) {
        throw new Error('Parsed patient data is not a valid array.')
      }

      const findNote = parsedData.filter((item) => item.id === noteID)

      if (findNote.length === 0) {
        throw new Error(`No patient note found with ID: ${noteID}`)
      }

      const treatmentData = findNote[0]
      const id = treatmentData?.id

      if (!id) {
        throw new Error('Note ID is missing in the matched note.')
      }

      let data = {}

      try {
        data = treatmentData?.data ? JSON.parse(treatmentData.data) : {}
      } catch (e) {
        console.error('Invalid JSON in treatmentData.data:', e)
        data = {}
      }

      if (type === 'Canceled') {
        data.NoteCancelReason = cancelReason
      }

      await PatientNoteDataCRUD('UPDATE', patientId.toString(), id.toString(), JSON.stringify(data), getFormattedDate().toString(), type, 'true')

      setCancelReason('')
    } catch (err) {
      console.error('Error in handlePatientNote:', err.message || err)
      setCancelReason('')
    }
  }

  const deletePatientNote = async (appointmentID, patientId) => {
    try {
      if (!appointmentID || !patientId) {
        throw new Error('Missing appointmentId or patientId.')
      }

      const appointmentFilterData = appointments.filter((item) => item?.data?.AppointmentID === appointmentID)

      if (appointmentFilterData.length === 0) {
        throw new Error(`No appointment found with ID: ${appointmentID}`)
      }

      const noteID = appointmentFilterData[0]?.data?.NoteID

      if (!noteID) {
        throw new Error(`NoteID not found for appointment ID: ${appointmentID}`)
      }

      const dataResponse = await PatientNoteDataCRUD('GET', patientId)

      if (!dataResponse?.d) {
        throw new Error('No data received from PatientNoteDataCRUD GET.')
      }

      const parsedResponse = JSON.parse(dataResponse.d)
      const parsedData = parsedResponse?.Data

      if (!Array.isArray(parsedData)) {
        throw new Error('Parsed patient data is not a valid array.')
      }

      const findNote = parsedData.filter((item) => item.id === noteID)

      if (findNote.length === 0) {
        throw new Error(`No patient note found with ID: ${noteID}`)
      }

      await PatientNoteDataCRUD('DELETE', patientID.toString(), noteID.toString())
    } catch (err) {
      console.error('Error in patient delete:', err.message || err)
    }
  }

  const getFacilitiesAppointment = async () => {
    let facilities = await FacilityCRUD('SELECT', {})
    let data = JSON.parse(facilities.d)

    facilities = data.Data
    facilities = facilities.filter((obj) => {
      if (obj.Days && obj.Days != '') {
        return obj
      }
    })
    setLocations(facilities)
  }

  const ensureScheduleBootstrap = async () => {
    if (hasScheduleBootstrapRef.current) return
    if (scheduleBootstrapPromiseRef.current) {
      await scheduleBootstrapPromiseRef.current
      return
    }

    scheduleBootstrapPromiseRef.current = (async () => {
      await Promise.all([getProviders(), getFacilitiesAppointment()])
      hasScheduleBootstrapRef.current = true
    })()

    try {
      await scheduleBootstrapPromiseRef.current
    } finally {
      scheduleBootstrapPromiseRef.current = null
    }
  }

  function formatDate(date) {
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')
    const yyyy = String(date.getFullYear())
    const hh = String(date.getHours()).padStart(2, '0')
    const min = String(date.getMinutes()).padStart(2, '0')
    return `${mm}/${dd}/${yyyy} ${hh}:${min}`
  }

  const handleAppointment = (appointmentID, type, patientId, patient) => {
    if (type === 'cancel') {
      setCancelModel(true)
    } else if (type === 'finish') {
      getSuperBillData(patientId)
      handleAppointmentSubmit('Finish', appointmentID, patientId)
      setTimeout(() => {
        handleAutoSchedule(patient, appointmentID)
      }, 1000)
    } else if (type === 'delete') {
      setDeleteModel(true)
    }

    setAppointmentID(appointmentID)
    setPatientId(patientId)
    setSuperBill([])
  }
  handleAppointmentActionRef.current = handleAppointment
  const stableHandleAppointment = useCallback((appointmentID, type, patientId, patient) => {
    handleAppointmentActionRef.current?.(appointmentID, type, patientId, patient)
  }, [])

  const handleSubmit = async (updateData, type = null) => {
    try {
      setIsPageLoading(true)

      const groupedData = []

      updateData.forEach((entry) => {
        const patientID = entry.current.Patient.value

        const existing = groupedData.find((item) => item.patientID === patientID)

        if (existing) {
          existing.appointmentDetails.push({ data: entry.data, current: entry.current })
        } else {
          groupedData.push({
            patientID,
            appointmentDetails: [{ data: entry.data, current: entry.current }],
          })
        }
      })

      await SaveExtra('Update', 'multiAppointmentsData', JSON.stringify(groupedData), allAppointment[0]?.Id || 388)

      await patientDataFn()
      if (type == 'Rescheduled') {
        toast.success('Patient Rescheduled Scheduled  Successfully', toastConfig)
      } else {
        toast.success('Patient Auto Scheduled  Successfully', toastConfig)
      }
      //   toast.success('Patient Auto Scheduled  Successfully', toastConfig)
    } catch (error) {
      toast.error('Failed to Auto Scheduled', toastConfig)
    } finally {
      setIsPageLoading(false)
      setSelectDate(null)
      setShowRescheduleModal(false)
      setSelectedProvider(null)
    }
  }

  const getAllProviderDetails = async (obj) => {
    const provider = {
      ID: obj.ID,
    }
    let providerData
    try {
      providerData = await fetchPatientData('SELECTALL', 'Provider', JSON.stringify(provider))
    } catch (error) {
      console.warn(`Unable to fetch provider details for provider ${obj.ID}:`, error)
    }

    const parsedProviderData = safeJsonParse(providerData?.d, {})
    let insuranceData = Array.isArray(parsedProviderData?.Insurance) ? parsedProviderData.Insurance : []
    let facilityData = Array.isArray(parsedProviderData?.Facility) ? parsedProviderData.Facility : []
    // setselectProviderInsurances(insuranceData)
    // setselectProviderFacilities(facilityData)

    obj['Insurances'] = insuranceData
    obj['Facilities'] = facilityData

    return obj
  }

  const getProviders = async () => {
    let providerData
    try {
      providerData = await fetchPatientData('SELECT', 'Provider', '{}')
    } catch (error) {
      console.warn('Unable to fetch providers:', error)
      setProviderSchedule({})
      return
    }

    let data = safeJsonParse(providerData?.d, {})
    providerData = Array.isArray(data?.Data) ? data.Data : []
    let newProviders = []
    for (let i = 0; i < providerData.length; i++) {
      let c = await getAllProviderDetails(providerData[i])
      newProviders.push(c)
    }

    const daysMap = {}

    // Iterate through the providers array
    newProviders.forEach((provider) => {
      // Iterate through each facility of the provider
      ;(provider.Facilities || []).forEach((facility) => {
        // Split the Days string into an array of days
        const daysArray = String(facility.Days || '').split(',')

        // For each day in the daysArray, add the provider to the corresponding entry in the map
        daysArray.forEach((day) => {
          day = day.trim()
          if (!day) return

          if (!daysMap[day]) {
            daysMap[day] = []
          }

          // Find if the provider already exists in the day's list
          let providerEntry = daysMap[day].find((p) => p.ID === provider.ID)
          if (!providerEntry) {
            // Add the provider if not already in the list for that day
            providerEntry = {
              ID: provider.ID,
              Name: provider.Name,
              Gender: provider.Gender,
              DOB: provider.DOB,
              Address: provider.Address,
              AddressDetails: provider.AddressDetails,
              Lat: provider.Lat,
              Lng: provider.Lng,
              Insurances: provider.Insurances,
              Facilities: [],
            }
            daysMap[day].push(providerEntry)
          }

          // Add the facility to the provider's list of facilities
          providerEntry.Facilities.push(facility)
        })
      })
    })

    setDaysMap(daysMap)
    return newProviders
  }

  const allData = useRef({})

  const handleSchedule = async (props, patient, oldData) => {
    if (!props?.StartTime || !props?.EndTime) return

    let scheduleAppointmentTime = await getSuperBillData(patient.PatientID)
    const weeksMatch = scheduleAppointmentTime?.match(/(\d+)\s*week/i)
    const weeksToAdd = weeksMatch ? parseInt(weeksMatch[1], 10) : 1
    let startTime = new Date(props.StartTime)
    let targetDate = new Date(startTime)
    targetDate.setDate(targetDate.getDate() + weeksToAdd * 7)
    const date = targetDate.toISOString().split('T')[0]
    const dayOptions = { weekday: 'long' }
    const day = targetDate.toLocaleDateString('en-US', dayOptions)

    try {
      const allDayData = daysMap[day]
      if (!allDayData) {
        console.warn(`No data found for ${day}.`)
        alert('No schedule available for this day.')
        return
      }

      allDayData.forEach((obj) => {
        obj.Facilities.forEach((facility) => {
          let tempFacility = locations.find((o) => o.ID == facility.FacilityID)
          facility['Name'] = tempFacility?.Name || ''
          facility['Type'] = tempFacility?.Type || ''
        })
      })

      const providerInfo = allDayData.filter((obj) => obj.Facilities.some((facility) => facility.Name === patient.FacilityName))
      if (providerInfo.length === 0) {
        console.warn(`No provider assigned to ${patient.FacilityName} on ${day}.`)
        alert('No providers are currently assigned to this facility on the selected day.')
        return
      } else {
        let provider = providerInfo.filter((item) => item?.ID === oldData?.current?.Provider?.value)

        if (provider.length === 0) {
          provider = providerInfo[0]
        } else {
          provider = provider[0]
        }
        allData.current.Provider = {
          value: provider?.ID,
          label: provider?.Name,
        }
        return targetDate
      }
    } catch (e) {
      console.error('Error processing schedule:', e)
    }
  }

  const handleAutoSchedule = async (item, appointmentID) => {
    await ensureScheduleBootstrap()
    const appointmentFilterData = appointments.filter((item) => item?.data?.AppointmentID === appointmentID)
    let patientInsurance = await PatientInsuranceFn('SELECT', '', item.PatientID)
    let tempAppointments = []
    let Insurance = {}
    try {
      patientInsurance = JSON.parse(patientInsurance.d)?.Data
      if (patientInsurance.length > 0) {
        Insurance = JSON.parse(patientInsurance[0].InsuranceDetails)
        Insurance = Insurance[0] || {}
      }
    } catch (e) {}
    try {
      let getPatientTime = await PatientNoteDataCRUD('GET', item.PatientID)
      getPatientTime = JSON.parse(getPatientTime.d)?.Data
      getPatientTime = getPatientTime.map((entry) => ({
        ...entry,
        getPatientTime: entry.getPatientTime ? JSON.parse(entry.getPatientTime) : {},
      }))
      if (getPatientTime) {
        let rawDate = getPatientTime.length > 0 ? getPatientTime[0].date : new Date().toISOString().split('T')[0]
        let formattedDate = rawDate.replaceAll('.', '/')

        let props = {
          StartTime: appointmentFilterData.length > 0 ? appointmentFilterData[0]?.data.StartTime : formattedDate,
          EndTime: formattedDate,
          PatientID: item.PatientID,
        }

        let filtered = await getAppointmentForAutoSchedule()

        let oldData
        if (filtered.length > 0) {
          filtered = filtered.filter((entry) => entry.patientID === item.PatientID && entry.appointmentDetails.some((appt) => appt.data?.Status === 'Signed' || appt.data?.Status === 'Finish'))
          oldData = filtered[0].appointmentDetails.at(-1)
        }

        if (oldData?.data?.isInFacility === false) {
          alert(`Patient ${item.FirstName} ${item.LastName || ''} is not in the facility and cannot be scheduled.`)
          return
        }
        let updateOtherDetails = {}

        const priorAuth = oldData?.data?.otherDetails?.priorAuth
        if (priorAuth?.isRequired) {
          let noOfTimes = parseInt(priorAuth?.details?.noOfTimes, 10)

          if (isNaN(noOfTimes) || noOfTimes <= 0) {
            alert(`Patient ${item.FirstName} ${item.LastName || ''} has no remaining prior auth and cannot be scheduled.`)
            if (oldData?.data) {
              oldData.data.isInFacility = false
            }
            return
          } else {
            updateOtherDetails = {
              ...oldData.data.otherDetails,
              priorAuth: {
                ...priorAuth,
                details: {
                  ...priorAuth.details,
                  noOfTimes: `${noOfTimes - 1}`,
                },
              },
            }
          }
        }

        let scheduledDate = await handleSchedule(props, item, oldData)
        let organizeDate = formatDate(scheduledDate)

        let facility = locations?.find((facility) => facility.Name === item.FacilityName)

        let localPatient = { value: item.PatientID, label: `${item.FirstName} ${item.LastName}` }
        let localFacility = { value: facility?.ID, label: facility?.Name }
        let localProvider = allData.current?.Provider || {}

        let data = {
          AppointmentName: `${item.FirstName} ${item.LastName}`,
          StartTime: organizeDate,
          EndTime: organizeDate.replace(':00', ':15').replace(':30', ':45'),
          PatientID: item.PatientID,
          StartTimezone: null,
          EndTimezone: null,
          Facility: facility?.Name || '',
          Insurance: Insurance || {},
          Status: 'Scheduled',
          Notes: '',
          RoomNumber: item.Room,
          RecurrenceRule: '',
          VisitReason: 'F/U (Follow Up)',
          isInFacility: oldData ? oldData?.data?.isInFacility || true : true,
          otherDetails: oldData ? updateOtherDetails || {} : {},
          AppointmentID: Date.now(),
          ProviderName: localProvider?.label || '',
          ScheduleBy: 'auto-manuel',
          // PatientStatus: filtered.length > 0 ? 'Follow Up' :  item?.Status || 'NewPatient'
          Type: 'Follow Up',
          CreatedDate: new Date(),
          // isDragged: oldData ? (oldData?.data?.isDragged || false) : false,
        }

        let details = {
          data,
          current: {
            Patient: localPatient,
            Facility: localFacility,
            Provider: localProvider,
            Insurance: Insurance,
          },
        }

        tempAppointments.push(details)
      } else {
        alert('Patient creation failed: Patient creation date not found. \n Hint: Try using manual-schedule to proceed.')
      }
    } catch (error) {
      console.error('Error fetching patient orders', error)
    }
    // Update appointments once at the end
    if (tempAppointments.length > 0) {
      setAppointments((prev) => {
        const allAppointments = [...prev, ...tempAppointments]
        handleSubmit(allAppointments)
        return allAppointments
      })
    }
  }

  useEffect(() => {
    if (showPatientAppointmentId) {
      GetSelectedPatientAppoint()
    }
  }, [showPatientAppointmentId])

  const getSuperBillData = async (patientID) => {
    try {
      let data = await PatientSuperBillData('Get_By_PatientId', '', '', '', patientID.toString())
      data = JSON.parse(data?.d)?.Data || []

      if (data?.length) {
        data = data.map((item) => ({
          Id: item.Id,
          Data: JSON.parse(item?.JsonData),
        }))
      }

      setSuperBill(data)

      const lastItem = data?.at(-1)?.Data?.followUpTime ?? null

      setScheduleTime(lastItem)
      return lastItem
    } catch (error) {
      console.error('Error fetching Super Bill data:', error)
    }
  }

  const [selectDate, setSelectDate] = useState(null)
  const [selectedProvider, setSelectedProvider] = useState(null)
  const [showRescheduleModal, setShowRescheduleModal] = useState(false)
  const [rescheduleData, setRescheduleData] = useState({
    appointmentID: null,
    patientID: null,
    patient: null,
    index: null,
  })
  const handleOpenReschedule = useCallback((nextRescheduleData) => {
    setRescheduleData(nextRescheduleData)
    setShowRescheduleModal(true)
  }, [])

  const handlePatientNoteStatus = async (type, appointmentID, patientId) => {
    try {
      if (!appointmentID || !patientId) {
        throw new Error('Missing appointmentId or patientId.')
      }

      const appointmentFilterData = appointments.filter((item) => item?.data?.AppointmentID === appointmentID)

      if (appointmentFilterData.length === 0) {
        throw new Error(`No appointment found with ID: ${appointmentID}`)
      }

      const noteID = appointmentFilterData[0]?.data?.NoteID

      if (!noteID) {
        throw new Error(`NoteID not found for appointment ID: ${appointmentID}`)
      }

      const dataResponse = await PatientNoteDataCRUD('GET', patientId)

      if (!dataResponse?.d) {
        throw new Error('No data received from PatientNoteDataCRUD GET.')
      }

      const parsedResponse = JSON.parse(dataResponse.d)
      const parsedData = parsedResponse?.Data

      if (!Array.isArray(parsedData)) {
        throw new Error('Parsed patient data is not a valid array.')
      }

      const findNote = parsedData.filter((item) => item.id === noteID)

      if (findNote.length === 0) {
        throw new Error(`No patient note found with ID: ${noteID}`)
      }

      const treatmentData = findNote[0]
      const id = treatmentData?.id

      if (!id) {
        throw new Error('Note ID is missing in the matched note.')
      }

      let data = {}

      try {
        data = treatmentData?.data ? JSON.parse(treatmentData.data) : {}
      } catch (e) {
        console.error('Invalid JSON in treatmentData.data:', e)
        data = {}
      }

      await PatientNoteDataCRUD('UPDATE', patientId.toString(), id.toString(), JSON.stringify(data), getFormattedDate().toString(), type, 'false')

      setCancelReason('')
    } catch (err) {
      console.error('Error in handlePatientNote:', err.message || err)
      setCancelReason('')
    }
  }

  const findDates = (allowedWeekdays) => {
    const weekdayMapping = {
      Sunday: 0,
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
    }
    const allowedWeekdayNumbers = allowedWeekdays.map((day) => weekdayMapping[day])
    let today = new Date()
    let matchingDates = []

    for (let i = 0; i < 16; i++) {
      let currentDate = new Date(today)
      currentDate.setDate(today.getDate() + i)

      let currentWeekday = currentDate.getDay()

      if (allowedWeekdayNumbers.includes(currentWeekday)) {
        matchingDates.push(currentDate.toString())
      }
    }
    return matchingDates
  }

  const handleRescheduleSubmit = (appt, provider, date) => {
    const updateStatus = handlePatientNoteStatus('Draft', appt?.data?.AppointmentID, appt?.current?.Patient?.value)

    let allChangedData = appt?.data

    let oldSchedulingData = appointments.filter((item) => item?.data?.AppointmentID === allChangedData?.AppointmentID)

    let current = {
      ...appt?.current,
      Provider: provider,
    }

    let data = {
      ...appt.data,
      Status: 'Rescheduled',
      StartTime: formatDate(date),
      EndTime: formatDate(date).replace(':00', ':15').replace(':30', ':45') || oldSchedulingData[0]?.data.EndTime || allChangedData.EndTime,
    }

    let FinalData = { data, current }
    setAppointments((appointments) => {
      const updatedAppointments = appointments.some((appointment) => appointment.data.AppointmentID === allChangedData.AppointmentID) ? appointments.map((appointment) => (appointment.data.AppointmentID === allChangedData.AppointmentID ? { data, current } : appointment)) : appointments
      handleSubmit(updatedAppointments, 'Rescheduled')
      return updatedAppointments
    })

    // setSelectDate(null)
    // setSelectedProvider(null)
  }

  const handleReschedule = (patient, appt, date) => {
    let facilityDays = locations?.filter((facility) => facility.Name === patient.FacilityName)[0]

    const dayOptions = { weekday: 'long' }
    const day = date?.toLocaleDateString('en-US', dayOptions)
    const dayData = daysMap[day] || []
    const allDay = dayData.map((obj) => {
      obj.Facilities.forEach((facility) => {
        const tempFacility = locations.find((o) => o.ID === facility.FacilityID)
        facility['Name'] = tempFacility?.Name || ''
        facility['Type'] = tempFacility?.Type || ''
      })
      return obj
    })

    let assignedProvider = selectedProvider

    let apptData = appointment.filter((item) => item.data.AppointmentID === appt)

    if (selectDate && selectedProvider && apptData.length > 0) {
      handleRescheduleSubmit(apptData[0], selectedProvider, date)
    }
  }

  useEffect(() => {
    if (showRescheduleModal) {
      ensureScheduleBootstrap()
    }
  }, [showRescheduleModal])

  const hasActiveSearch = Boolean(normalizeSearchValue(filterData.searchValue))
  const hasEmptyGroupedAppointments = !groupedUpdatedFilteredPatients || Object.keys(groupedUpdatedFilteredPatients).length === 0
  const shouldShowAppointmentsLoader = isPageLoading || (!hasActiveSearch && hasEmptyGroupedAppointments && !hasNotAppointment)
  return (
    <div>
      {showPatientInfo ? (
        <PatientInfo
          creatingNewNote={creatingNewNote}
          setCreatingNewNote={setCreatingNewNote}
          childformData={childformData}
          setChildFormData={setChildFormData}
          saveDiagnosisData={saveDiagnosisData}
          noteStatus={noteStatus}
          patientID={patientIDClicked}
          patientData={rowClicked}
          setRowClicked={setRowClicked}
          setAppointmentDetails={false}
          noteStructureRef={noteStructureRef}
          dataCallType={dataCallType}
          setEditDataId={setEditDataId}
          setDataCallType={setDataCallType}
          noteID={noteID}
          setDataId={setDataId}
          handleAddNoteId={handleAddNoteId}
          appointments={appointments}
        />
      ) : setDataId ? (
        <PatientInfo
          creatingNewNote={creatingNewNote}
          setCreatingNewNote={setCreatingNewNote}
          childformData={childformData}
          setChildFormData={setChildFormData}
          setAppointmentDetails={false}
          saveDiagnosisData={saveDiagnosisData}
          noteStatus={noteStatus}
          patientID={patientIDClicked}
          patientData={rowClicked}
          setRowClicked={setRowClicked}
          noteStructureRef={noteStructureRef}
          dataCallType={dataCallType}
          setDataId={setDataId}
          setDataCallType={setDataCallType}
          noteID={noteID}
          setEditDataId={setEditDataId}
          handleAddNoteId={handleAddNoteId}
          appointments={appointments}
        />
      ) : (
        <div ref={allChartsThemeRef} className="position-relative patient-theme-page all-charts-theme" data-theme={localStorage.getItem('provio_theme') || 'dark'}>
          <div className="patient-page-wrapper page-wrapper patient-theme-shell dashboard-dark-theme all-charts-shell">
            <div className="page-header mb-3">
              <div className="d-flex align-items-center gap-2 patient-theme-toolbar">
                <div className="search-input search-dark dashboard-dark-search">
                  <SearchIcon />
                  <input
                    onChange={(e) => {
                      updateFilterData((prev) => ({ ...prev, searchValue: e.target.value }))
                    }}
                    type="text"
                    placeholder="Search by Patient"
                  />
                </div>
                <div className="position-relative ">
                  <Button className="d-flex align-items-center gap-2 dashboard-dark-filter-button" variant="outlined" size="large" onClick={() => setShowFilter(!showFilter)}>
                    <FunnelPlotOutlined />
                    Filter
                  </Button>

                  {showFilter && (
                    <motion.div
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 4 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      style={{
                        width: '400px',
                        zIndex: 50,
                        borderRadius: '10px',
                      }}
                      className="position-absolute gap-1 d-flex flex-column top-full left-0 z-10 shadow-lg p-4 rounded-md dashboard-dark-filter-popover patient-theme-filter-popover"
                    >
                      <div>
                        <CFormLabel>Select Facility</CFormLabel>
                        <Select
                          placeholder="Select"
                          name="facility"
                          options={facilities}
                          value={facilities.filter((fac) => filterData.facility.includes(fac.value))}
                          isMulti
                          isClearable
                          onChange={(selectedOptions) =>
                            updateFilterData((prev) => ({
                              ...prev,
                              facility: selectedOptions ? selectedOptions.map((option) => option.value) : [],
                            }))
                          }
                          className="basic-multi-select"
                          classNamePrefix="select"
                        />
                      </div>

                      <div className="mt-2">
                        <CFormLabel>Select Patient Status</CFormLabel>
                        <Select
                          name="status"
                          isMulti
                          placeholder="Select"
                          options={selectStatusOption}
                          isClearable
                          value={selectStatusOption.filter((option) => filterData.status.includes(option.value))}
                          onChange={(selectedOptions) =>
                            updateFilterData((prev) => ({
                              ...prev,
                              status: selectedOptions ? selectedOptions.map((option) => option.value) : [],
                            }))
                          }
                          className="basic-multi-select"
                          classNamePrefix="select"
                        />
                      </div>

                      <div className="mt-2">
                        <CFormLabel>Select Providers</CFormLabel>
                        <Select
                          name="status"
                          isMulti
                          placeholder="Select Note Status"
                          isClearable
                          options={providerList.map((provider) => ({
                            value: provider.Name,
                            label: provider.Name,
                          }))}
                          value={providerList.filter((provider) => filterData.providers.includes(provider.Name)).map((provider) => ({ value: provider.Name, label: provider.Name }))}
                          onChange={(selectedOptions) =>
                            updateFilterData((prev) => ({
                              ...prev,
                              providers: selectedOptions ? selectedOptions.map((option) => option.value) : [],
                            }))
                          }
                          className="basic-multi-select"
                          classNamePrefix="select"
                        />
                      </div>

                      <div className="mt-2">
                        <CFormLabel>Select Note Status</CFormLabel>
                        <Select
                          name="status"
                          isMulti
                          placeholder="Select Providers"
                          isClearable
                          value={notesStatusOptions.filter((option) => filterData.noteStatuses.includes(option.value)).map((option) => ({ value: option.value, label: option.label }))}
                          options={notesStatusOptions}
                          onChange={(selectedOptions) =>
                            updateFilterData((prev) => ({
                              ...prev,
                              noteStatuses: selectedOptions ? selectedOptions.map((option) => option.value) : [],
                            }))
                          }
                          className="basic-multi-select"
                          classNamePrefix="select"
                        />
                      </div>

                      <div className="mt-2" style={{ position: 'relative', display: 'inline-block' }}>
                        <CFormLabel>Select DOB</CFormLabel>
                        <div>
                          <CustomDatePickingFormat
                            type="date"
                            color="primary"
                            value={filterData.date || ''}
                            aria-describedby="exampleFormControlInputHelpInline"
                            onChange={(e) => {
                              updateFilterData((p) => ({
                                ...p,
                                date: e, // Keep the date in "YYYY-MM-DD" format
                              }))
                            }}
                          />
                        </div>
                        {/* <CFormInput
                          type="date"
                          color="primary"
                          value={filterData.date || ''}
                          aria-describedby="exampleFormControlInputHelpInline"
                          onChange={(e) => {
                            setFilterData((p) => ({
                              ...p,
                              date: e.target.value, // Keep the date in "YYYY-MM-DD" format
                            }))
                          }}
                        /> */}
                        {/* {filterData.date && (
                          <button
                            className="d-flex align-items-center justify-content-center"
                            onClick={() => setFilterData((p) => ({ ...p, date: '' }))}
                            style={{
                              position: 'absolute',
                              left: '112px',
                              top: '52%',
                              width: '20px',
                              height: '20px',
                              transform: 'translateY(-50%)',
                              background: 'black',
                              border: 'none',
                              cursor: 'pointer',
                              borderRadius: '100px',
                              color: '#fff',
                            }}
                          >
                            <X className="" strokeWidth={3} size={30} />
                          </button>
                        )} */}
                      </div>

                      <div className="mt-2" style={{ position: 'relative', display: 'inline-block' }}>
                        <CFormLabel>Select Scheduled Date</CFormLabel>
                        <div>
                          <CustomDatePickingFormat
                            type="date"
                            color="primary"
                            value={filterData.scheduleDate || ''}
                            aria-describedby="exampleFormControlInputHelpInline"
                            onChange={(e) => {
                              updateFilterData((p) => ({
                                ...p,
                                scheduleDate: e, // Keep the date in "YYYY-MM-DD" format
                              }))
                            }}
                          />
                        </div>
                        {/* <CFormInput
                          type="date"
                          color="primary"
                          value={filterData.scheduleDate || ''}
                          aria-describedby="exampleFormControlInputHelpInline"
                          onChange={(e) => {
                            setFilterData((p) => ({
                              ...p,
                              scheduleDate: e.target.value, // Keep the date in "YYYY-MM-DD" format
                            }))
                          }}
                        /> */}
                        {/* {filterData.scheduleDate && (
                          <button
                            className="d-flex align-items-center justify-content-center"
                            onClick={() => setFilterData((p) => ({ ...p, scheduleDate: '' }))}
                            style={{
                              position: 'absolute',
                              left: '112px',
                              top: '52%',
                              width: '20px',
                              height: '20px',
                              transform: 'translateY(-50%)',
                              background: 'black',
                              border: 'none',
                              cursor: 'pointer',
                              borderRadius: '100px',
                              color: '#fff',
                            }}
                          >
                            <X className="" strokeWidth={3} size={30} />
                          </button>
                        )} */}
                      </div>

                      <Button className="mt-2" onClick={() => setShowFilter(false)} variant="outlined">
                        Close
                      </Button>
                    </motion.div>
                  )}
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div className="display-total-patient dashboard-dark-stat">
                  <span className="mx-1">{patientInfoCount?.newPatient || 0}</span> NP
                </div>
                <div className="display-total-patient dashboard-dark-stat">
                  <span className="mx-1">{patientInfoCount?.followUp || 0}</span> FU
                </div>
                <div className="display-total-patient dashboard-dark-stat">
                  <span className="mx-1">{patientCount}</span> Patients
                </div>
                {/* <Button variant="contained" onClick={handleonClick}>+ Add Patient</Button> */}
                <div className="d-flex align-items-center justify-content-end gap-1 mx-2 ">
                  <CButton
                    onClick={() => {
                      setBoxView('list')
                    }}
                    style={{
                      height: '40px',
                      padding: '5px 10px',
                    }}
                    color={boxView === 'list' ? 'primary' : 'light'}
                    className={`d-flex align-items-center justify-content-center patient-theme-view-toggle ${boxView === 'list' ? 'patient-theme-view-toggle--active' : ''}`}
                  >
                    <Table2 size={16} strokeWidth={1.25} />
                    <div style={{ marginLeft: '8px', fontSize: '14px', fontWeight: '500' }}>List</div>
                  </CButton>
                  <CButton
                    onClick={() => {
                      setBoxView('details')
                    }}
                    style={{
                      height: '40px',
                      padding: '5px 10px',
                      marginRight: '10px',
                    }}
                    color={boxView === 'details' ? 'primary' : 'light'}
                    className={`d-flex align-items-center justify-content-center patient-theme-view-toggle ${boxView === 'details' ? 'patient-theme-view-toggle--active' : ''}`}
                  >
                    <AlignLeft size={16} strokeWidth={1.25} />
                    <div style={{ marginLeft: '8px', fontSize: '14px', fontWeight: '500' }}>Details</div>
                  </CButton>
                </div>
              </div>
            </div>

            {boxView === 'details' && (
              <motion.div className="custom-card-wrapper all-charts-card-wrapper">
                {updatedFilteredPatients.length > 0 &&
                  updatedFilteredPatients.map((patient, index) => {
                    // let provider = appointment.filter(item => item.current.Patient.value === patient.PatientID);
                    const patientAppointments = appointmentsByPatientId.get(String(patient.PatientID ?? '').trim()) || []

                    // let providerName = provider[0]?.current?.Provider?.label;
                    // let scheduleDate = provider[0]?.data?.StartTime
                    // scheduleDate = scheduleDate?.split(" ")
                    let statusPatientAppointment
                    let EndTimezone

                    const scheduledAppointment = patientAppointments.find((p) => {
                      const status = p?.data?.Status
                      EndTimezone = p?.data?.EndTimezone
                      statusPatientAppointment = p?.data?.Status
                      const scheduleBy = p?.data?.ScheduleBy
                      const type = p?.data?.Type
                      const isInFacility = p?.data?.isInFacility
                      const priorAuth = p?.data?.otherDetails?.priorAuth?.isRequired
                      let priorAuthTime = 0
                      if (priorAuth) {
                        priorAuthTime = parseInt(p?.data?.otherDetails?.priorAuth?.details?.noOfTimes, 10)
                      }

                      // const isManualOrFollowUp = scheduleBy !== 'auto' || type === 'Follow Up';
                      const isManualOrFollowUp = scheduleBy !== 'auto' || (type === 'Follow Up' && isInFacility && priorAuth && priorAuthTime > 0)
                      const isScheduledOrRescheduled = status === 'Scheduled' || status === 'Rescheduled' || status === 'Canceled'

                      return isScheduledOrRescheduled && isManualOrFollowUp
                    })

                    if (!scheduledAppointment) return null

                    const providerName = scheduledAppointment?.current?.Provider?.label
                    const scheduleDate = scheduledAppointment?.data?.StartTime?.split(' ')[0]
                    const Type = scheduledAppointment?.data?.Type

                    return (
                      <motion.div
                        key={`${updatedFilteredPatients?.length}-${index}`}
                        initial={{
                          opacity: 0,
                        }}
                        animate={{
                          opacity: 1,
                        }}
                        exit={{
                          opacity: 0,
                        }}
                        transition={{
                          duration: 0.2,
                        }}
                        className="custom-card"
                      >
                        <div className="card-header">
                          <div>
                            <div className="d-flex align-items-center gap-2">
                              <p className="name-block">
                                {patient.FirstName + ' ' + patient.MiddleName + ' ' + patient.LastName}
                                <span
                                  style={{
                                    color: Type === 'New' ? 'green' : Type === 'Follow Up' ? 'orange' : 'skyblue',
                                    fontWeight: 'bold',
                                    fontSize: '0.6em',
                                  }}
                                >
                                  ({Type === 'Follow Up' ? 'Follow Up' : Type === 'New' ? 'New Patient' : Type === 'Re Admit' ? 'Re Admit' : ''})
                                </span>
                              </p>
                            </div>
                            <div className="bullet-points">
                              <span>{patient.Gender}</span>
                              {patient?.DOB && (
                                <span>
                                  DOB:{' '}
                                  {new Date(patient?.DOB).toLocaleDateString('en-US', {
                                    month: '2-digit',
                                    day: '2-digit',
                                    year: 'numeric',
                                  })}{' '}
                                </span>
                              )}
                              <span>{patient.Age} Years</span>
                            </div>
                          </div>
                          {statusPatientAppointment !== 'Canceled' && (
                            <div className="button-wrapper d-flex align-items-center gap-2">
                              {/* <a href="/home/scheduler">
                                <CButton variant="outline" color="warning" className="px-2">
                                  <HiPencil size={20} />
                                </CButton>
                              </a>
                               */}
                              <CButton onClick={() => handleOpenAppointmentDetails(patient, scheduledAppointment?.data?.AppointmentID)} variant="outline" color="success" className="px-2">
                                <HiEye size={20} />
                              </CButton>
                              <CButton onClick={() => openTreatmentNote(patient, scheduledAppointment?.data?.AppointmentID)} variant="outline" color="dark" className="px-2">
                                <FilePenLineIcon size={20} />
                              </CButton>
                              {/* <CDropdown>
                                <CDropdownToggle
                                  style={{
                                    padding: 0,
                                    margin: 0,
                                  }}
                                  color=""
                                >
                                  <CButton variant="outline" color="dark" className="calendar-button px-2">
                                    <CalendarCheck size={20} />
                                  </CButton>
                                </CDropdownToggle>
                                <CDropdownMenu>
                                  <CDropdownItem onClick={() => handleAppointment(scheduledAppointment?.data?.AppointmentID, 'cancel', patient?.PatientID, patient)}>Cancel</CDropdownItem>
                                  <CDropdownItem onClick={() => handleAppointment(scheduledAppointment?.data?.AppointmentID, 'finish', patient?.PatientID, patient)}>Finish</CDropdownItem>
                                  <CDropdownItem
                                    onClick={() => {
                                      setRescheduleData({
                                        appointmentID: scheduledAppointment?.data?.AppointmentID,
                                        patientID: patient?.PatientID,
                                        patient: patient,
                                        index: index,
                                      })
                                      setShowRescheduleModal(true)
                                    }}
                                  >
                                    Reschedule
                                  </CDropdownItem>
                                  <CDropdownItem onClick={() => handleAppointment(scheduledAppointment?.data?.AppointmentID, 'delete', patient?.PatientID, patient)}>Delete</CDropdownItem>
                                </CDropdownMenu>
                              </CDropdown> */}
                            </div>
                          )}
                          {statusPatientAppointment === 'Canceled' && (
                            <div className="button-wrapper d-flex align-items-center gap-2">
                              <CButton onClick={() => handleOpenAppointmentDetails(patient, scheduledAppointment?.data?.AppointmentID)} variant="outline" color="success" className="px-2">
                                <HiEye size={20} />
                              </CButton>
                              <CButton
                                variant="outline"
                                color="dark"
                                className="calendar-button px-2"
                                onClick={() => {
                                  setRescheduleData({
                                    appointmentID: scheduledAppointment?.data?.AppointmentID,
                                    patientID: patient?.PatientID,
                                    patient: patient,
                                    index: index,
                                  })
                                  setShowRescheduleModal(true)
                                }}
                              >
                                <Undo2 size={20} />
                              </CButton>
                            </div>
                          )}
                        </div>
                        <div className="contact-wrapper w-100  d-flex  px-3">
                          <div>
                            <p className="title">Provider Name</p>
                            <p className="value">{providerName}</p>
                          </div>
                          <div>
                            <p className="title">Schedule Date</p>
                            <p className="value">{scheduleDate}</p>
                          </div>
                          {statusPatientAppointment === 'Canceled' && (
                            <div>
                              <p className="title">Cancellation Date</p>
                              <p className="value">{EndTimezone?.split(' ')[0]}</p>
                            </div>
                          )}

                          {statusPatientAppointment === 'Finished' && (
                            <div>
                              <p className="title">Finished Date</p>
                              <p className="value">{EndTimezone?.split(' ')[0]}</p>
                            </div>
                          )}
                          {/* <div>
                                                        <p className="title">Note Status</p>
                                                        <p className="value">{patient.noteStatus || ""}</p>
                                                    </div> */}
                        </div>
                        <div className="card-footer w-100 d-flex justify-content- align-items-start">
                          <div className="facility mw-100">
                            <p className="title">FACILITY NAME</p>
                            <p className="value">
                              {patient.FacilityName}
                              {patient.facilityParentCompany && ` - ${patient.facilityParentCompany}`}
                            </p>
                          </div>
                          <div className="room-count">
                            <p className="title">ROOM</p>
                            <p className="value">{patient.Room ?? ''}</p>
                          </div>
                        </div>
                      </motion.div>
                    )
                  })}
              </motion.div>
            )}
            {boxView === 'list' && (
              <>
                <AllAppointmentsList
                  groupedUpdatedFilteredPatients={groupedUpdatedFilteredPatients}
                  appointmentsByPatientId={appointmentsByPatientId}
                  onViewPatient={handleClicksPatiens}
                  onOpenAppointmentDetails={handleOpenAppointmentDetails}
                  onOpenNote={openTreatmentNote}
                  onAppointmentAction={stableHandleAppointment}
                  onOpenReschedule={handleOpenReschedule}
                />
                {!shouldShowAppointmentsLoader && Object.entries(groupedUpdatedFilteredPatients).length === 0 && <h6 className="d-flex justify-content-center text-secondary">No Patient found</h6>}
              </>
            )}

            {/* <CustomAgGrid callFrom="Patient" rowClickedFn={patientSelected} options={patientData} btnList={buttonList}></CustomAgGrid> */}

            <CModal
              size="lg"
              visible={patientModelVisible}
              className="dashboard-dark-modal patient-theme-modal"
              onClose={() => {
                setPatientModalVisible(false)
                setIsNoteModalLoading(false)
                setPatientIDClicked(null)
                setRowClicked(null)
                setPatientAppointmentID(null)
                setShowNoteJustThisId(null)
              }}
            >
              {/* patientData, patientID, setPatientModalVisible, setDataCallType, setShowPatientInfo, setEditDataId, setNoteID, patientSelected, childformData, setChildFormData, saveDiagnosisData */}
              <CModalBody className="view-data-modal patient-theme-modal-body position-relative" style={{ padding: '2rem', borderRadius: '2rem', overflow: 'auto', minHeight: '18rem' }}>
                {isNoteModalLoading && (
                  <div
                    className="d-flex flex-column align-items-center justify-content-center"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: 'var(--ui-bg-app)',
                      zIndex: 2,
                      gap: '0.75rem',
                    }}
                  >
                    <CSpinner color="primary" />
                    <span className="text-secondary">Loading note details...</span>
                  </div>
                )}
                {hasSelectedNotePatient &&
                  <PatientPopSignedChart
                    key={`${patientIDClicked}-${patientAppointmentID}`}
                    showNoteJustThisId={showNoteJustThisId}
                    creatingNewNote={creatingNewNote}
                    setCreatingNewNote={setCreatingNewNote}
                    noteStatus={noteStatus}
                    setNoteStatus={setNoteStatus}
                    setAppointmentDetails={true}
                    setNoteID={setNoteID}
                    childformData={childformData}
                    setChildFormData={setChildFormData}
                    saveDiagnosisData={saveDiagnosisData}
                    patientID={patientIDClicked}
                    patientData={rowClicked}
                    noteStructureRef={noteStructureRef}
                    setPatientModalVisible={setPatientModalVisible}
                    dataCallType={dataCallType}
                    setDataCallType={setDataCallType}
                    setEditDataId={setEditDataId}
                    setShowPatientInfo={setShowPatientInfo}
                    setAllSelectedDiagnosis={setAllSelectedDiagnosis}
                    patientAppointmentID={patientAppointmentID}
                    appointment={appointment}
                    viewPatientDetail={viewPatientDetail}
                    handleAddNoteId={handleAddNoteId}
                    allAppointment={allAppointment}
                    onInitialLoadSettled={() => setIsNoteModalLoading(false)}
                  />
                  //    <PatientNoteViewInModalFreeType
                  //       patientID={patientID}
                  // statusPatientNoteWasFollowUp={noteStatus && noteStatus === 'Follow Up' ? true : false}
                  // showHrsInTreatment={true}
                  // setDataId={setDataId}
                  // setRowClicked={setRowClicked}
                  // showTreatmentAsAccordion={true}

                  // showComment={showComment}
                  // submitNotes={submitNotes}
                  // setModalVisible={setModalVisible}
                  // formData={patientData}
                  // diagnosis={diagnosisData}

                  // noteStructureRef={noteStructureRef}
                  // selectedRisks={selectedRisks}
                  // setSelectedRisks={setSelectedRisks}
                  // selectedDifferentialDiagnosis={selectedDifferentialDiagnosis}
                  // setSelectedDifferentialDiagnosis={setSelectedDifferentialDiagnosis}

                  // medication={medicationList}
                  // setMedication={setMedicationList}
                  // patientTreatments={patientTreatments}

                  // allergies={allergiesList}
                  // setAllergies={setAllergiesList}
                  // patientDiagnosis={patientDiagnosis}
                  // MasterDiagnosisData={MasterDiagnosisData}
                  // imagingTypeList={imagingTypeList}
                  // imaging={imagingList}
                  // setImaging={setImagingList}
                  // activeTab={historyPersonalHistoryActiveTab}
                  // setActiveTab={setHistoryPersonalHistoryActiveTab}
                  //     patientData={{
                  //       ...patientData,
                  //       Status: noteID === 0 ? "NewPatient" : "Follow Up"
                  //     }}
                  // patientSidebarActiveTab={activeTab}
                  // personalHistory={histories}
                  // setPersonalHistory={setHistories}
                  // allLabs={allLabs}
                  // noteID={noteID}

                  // showToolbar={true}
                  // addendumData={addendum}
                  // showDataIfNoteIsFollowUp={noteID && noteID >= 0 ? true : false}
                  //   />
                }
              </CModalBody>
              <CModalFooter className="dashboard-dark-modal-footer">
                <Button variant="outlined" onClick={() => setPatientModalVisible(false)}>
                  Close
                </Button>
              </CModalFooter>
            </CModal>

            <CModal className="dashboard-dark-modal patient-theme-modal" visible={deleteVisible} onClose={() => setDeleteVisible(false)}>
              <CModalHeader className="dashboard-dark-modal-header">
                <CModalTitle>Warning</CModalTitle>
              </CModalHeader>
              <CModalBody className="dashboard-dark-modal-body">
                <p>Are you sure you want to delete?</p>
              </CModalBody>

              <CModalFooter className="dashboard-dark-modal-footer">
                <CButton
                  onClick={() => {
                    setDeleteVisible(false)
                  }}
                  className="text-white"
                  color="secondary"
                >
                  Close
                </CButton>
                <CButton onClick={handleDeletePatient} className="text-white" color="danger">
                  Delete
                </CButton>
              </CModalFooter>
            </CModal>

            <CModal
              className="rounded dashboard-dark-modal patient-theme-modal"
              alignment="center"
              onClose={() => {
                setShowPatientAppointmentId(null)
                setActiveKey(2)
              }}
              visible={showPatientAppointmentId !== null}
              aria-labelledby="VerticallyCenteredExample"
            >
              <CModalHeader className="dashboard-dark-modal-header">
                <CModalTitle className="" style={{ fontSize: '20px!important' }} id="VerticallyCenteredExample">
                  Appointment Details
                </CModalTitle>
              </CModalHeader>
              <CModalBody className="w-100 dashboard-dark-modal-body">
                <CNav className="d-flex align-items-center justify-content-center w-100" variant="pills" role="tablist">
                  <CNavItem>
                    <CNavLink active={activeKey === 2} onClick={() => setActiveKey(2)}>
                      Insurances
                    </CNavLink>
                  </CNavItem>
                  <CNavItem className="">
                    <CNavLink active={activeKey === 1} onClick={() => setActiveKey(1)}>
                      Other Details
                    </CNavLink>
                  </CNavItem>
                </CNav>
                <CTabContent>
                  <CTabPane role="tabpanel" aria-labelledby="home-tab" visible={activeKey === 1}>
                    <div style={{ widht: '100%!important' }} className="d-flex py-2 w-100 align-items-center gap-4 justify-content-between flex-wrap w-full">
                      {patientAppointment.length > 0 ? (
                        <div>
                          {scheduledPatientAppointments.map((item, idx) => {
                              return (
                                <div key={idx} className="w-100">
                                  <div
                                    style={{
                                      display: 'grid!important',
                                      gridTemplateColumns: '1fr 1fr 1fr',
                                      gridGap: '10px',
                                    }}
                                    className="w-100"
                                  >
                                    <div className="d-flex gap-2">
                                      <p className="title fw-bold">Prior Auth Numberr: </p>
                                      <p className="fw-light">{item?.data?.otherDetails?.priorAuth?.details?.priorAuthNo ?? ''}</p>
                                    </div>
                                    <div className="d-flex gap-2">
                                      <p className="title fw-bold">Number of times: </p>
                                      <p className="fw-light">{item?.data?.otherDetails?.priorAuth?.details?.noOfTimes ?? ''}</p>
                                    </div>
                                    <div className="d-flex gap-2">
                                      <p className="title fw-bold">Expiry Date: </p>
                                      {item?.data?.otherDetails?.priorAuth?.details?.expDate && (
                                        <p className="fw-light">
                                          {new Date(item?.data?.otherDetails?.priorAuth?.details?.expDate.split('T')[0]).toLocaleDateString('en-US', {
                                            month: '2-digit',
                                            day: '2-digit',
                                            year: 'numeric',
                                          })}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                        </div>
                      ) : (
                        <div>
                          <div className="w-100">
                            <div
                              style={{
                                display: 'grid!important',
                                gridTemplateColumns: '1fr 1fr 1fr',
                                gridGap: '10px',
                              }}
                              className="w-100"
                            >
                              <div className="d-flex gap-2">
                                <p className="title fw-bold">Prior Auth Number: </p>
                                <p className="fw-light">{''}</p>
                              </div>
                              <div className="d-flex gap-2">
                                <p className="title fw-bold">Number of times: </p>
                                <p className="fw-light">{''}</p>
                              </div>
                              <div className="d-flex gap-2">
                                <p className="title fw-bold">Expiry Date: </p>
                                <p className="fw-light">{''}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </CTabPane>
                  <CTabPane role="tabpanel" aria-labelledby="home-tab" visible={activeKey === 2}>
                    <div style={{ widht: '100%!important' }} className="d-flex py-2 w-100 align-items-center gap-4 justify-content-between flex-wrap w-full">
                      {patientAppointment.length > 0 ? (
                        <div>
                          {patientAppointment
                            .filter((item) => item.data.Status === 'Scheduled' || item.data.Status === 'Rescheduled')
                            .map((item, idx) => {
                              return (
                                <div key={idx} className="w-100">
                                  <div
                                    style={{
                                      display: 'grid!important',
                                      gridTemplateColumns: '1fr 1fr 1fr',
                                      gridGap: '10px',
                                    }}
                                    className="w-100"
                                  >
                                    <div className="d-flex gap-2">
                                      <p className="title fw-bold">Insurance Type</p>
                                      <p className="fw-light">{item?.data?.Insurance?.Type ?? ''}</p>
                                    </div>
                                    <div className="d-flex gap-2">
                                      <p className="title fw-bold">Insurance Sub Type</p>
                                      <p className="fw-light">{item?.data?.Insurance?.SubType ?? ''}</p>
                                    </div>
                                    <div className="d-flex gap-2">
                                      <p className="title fw-bold">Policy Number: </p>
                                      <p className="fw-light">{item?.data?.Insurance?.PolicyNumber ?? ''}</p>
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                        </div>
                      ) : (
                        <div>
                          <div className="w-100">
                            <div
                              style={{
                                display: 'grid!important',
                                gridTemplateColumns: '1fr 1fr 1fr',
                                gridGap: '10px',
                              }}
                              className="w-100"
                            >
                              <div className="d-flex gap-2">
                                <p className="title fw-bold">Policy Number: </p>
                                <p className="fw-light">{''}</p>
                              </div>
                              <div className="d-flex gap-2">
                                <p className="title fw-bold">Insurance Type</p>
                                <p className="fw-light">{''}</p>
                              </div>
                              <div className="d-flex gap-2">
                                <p className="title fw-bold">Insurance Sub Type</p>
                                <p className="fw-light">{''}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </CTabPane>
                </CTabContent>
              </CModalBody>
            </CModal>

            <CModal className="dashboard-dark-modal patient-theme-modal" visible={showRescheduleModal} onClose={() => setShowRescheduleModal(false)} size="mds">
              <CModalHeader className="dashboard-dark-modal-header" closeButton>
                <CModalTitle>Reschedule Appointment</CModalTitle>
              </CModalHeader>
              <CModalBody className="dashboard-dark-modal-body">
                {rescheduleData &&
                  (() => {
                    const patient = rescheduleData.patient
                    const facilityDays = locations?.find((facility) => facility.Name === patient?.FacilityName)

                    const allowedDates = findDates(facilityDays?.Days?.split(',') || [])
                    return <RescheduleDatePicker allowedDates={allowedDates} daysMap={daysMap} locations={locations} selectedDate={selectDate} selectedProvider={selectedProvider} setSelectDate={setSelectDate} setSelectedProvider={setSelectedProvider} facilityDays={facilityDays} />
                  })()}
              </CModalBody>
              <CModalFooter className="dashboard-dark-modal-footer">
                <CButton variant="outline" color="secondary" onClick={() => setShowRescheduleModal(false)}>
                  Close
                </CButton>
                <CButton variant="outline" color="primary" onClick={() => handleReschedule(rescheduleData?.patient, rescheduleData?.appointmentID, selectDate)}>
                  Re-schedule
                </CButton>
              </CModalFooter>
            </CModal>

            <CModal className="dashboard-dark-modal patient-theme-modal" visible={cancelModel} onClose={() => setCancelModel(false)}>
              <CModalHeader className="dashboard-dark-modal-header">
                <CModalTitle>Cancel Appointment</CModalTitle>
              </CModalHeader>

              <CModalBody className="dashboard-dark-modal-body">
                <CFormTextarea
                  id="cancelReason"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Type your cancellation reason here..."
                  className="rounded-3"
                  style={{
                    height: '140px',
                    padding: '1rem',
                    fontSize: '1rem',
                    // resize: 'none',
                  }}
                />
              </CModalBody>

              <CModalFooter className="dashboard-dark-modal-footer">
                <CButton color="secondary" variant="outline" onClick={() => setCancelModel(false)}>
                  Close
                </CButton>
                <CButton onClick={() => handleAppointmentCancel('Canceled')} className="text-white" color="danger">
                  Proceed to Cancel
                </CButton>
              </CModalFooter>
            </CModal>

            <CModal className="dashboard-dark-modal patient-theme-modal" visible={finishModel} onClose={() => setFinishModel(false)}>
              <CModalHeader className="dashboard-dark-modal-header">
                <CModalTitle>Finish Appointment</CModalTitle>
              </CModalHeader>
              <CModalBody className="dashboard-dark-modal-body">
                <p>Are you sure you want to finish this appointment?</p>
              </CModalBody>

              <CModalFooter className="dashboard-dark-modal-footer">
                <CButton
                  onClick={() => {
                    setFinishModel(false)
                  }}
                  color="secondary"
                  variant="outline"
                >
                  Close
                </CButton>
                <CButton
                  onClick={() => {
                    handleAppointmentSubmit('Finish')
                  }}
                  className="text-white"
                  color="success"
                >
                  Finish Appointment
                </CButton>
              </CModalFooter>
            </CModal>

            <CModal className="dashboard-dark-modal patient-theme-modal" visible={deleteModel} onClose={() => setDeleteModel(false)}>
              <CModalHeader className="dashboard-dark-modal-header">
                <CModalTitle>Delete Appointment</CModalTitle>
              </CModalHeader>
              <CModalBody className="dashboard-dark-modal-body">
                <p>Are you sure you want to Delete this appointment?</p>
              </CModalBody>

              <CModalFooter className="dashboard-dark-modal-footer">
                <CButton color="secondary" variant="outline" onClick={() => { setDeleteModel(false) }}>
                  Cancel
                </CButton>
                <CButton color="danger" className="hrm-modal-danger-btn" onClick={() => { handleDeleteSubmit() }}>
                  Delete
                </CButton>
              </CModalFooter>
            </CModal>
          </div>

          {/* <Pageloader isLoading={isPageLoading} /> */}
          <Pageloader isLoading={shouldShowAppointmentsLoader} />
        </div>
      )}
    </div>
  )
}
export default AllAppointments2
